/* eslint-disable max-len */
import {
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { Modal, message } from 'antd';

import { Tour } from 'app/types';
import { addParamsToUrl } from 'app/utils';
import { getLastId } from 'app/helpers';
import { useAppDispatch, useModal, useSize } from 'app/hooks';
import { useFolders } from 'app/hooks/useFolders';
import { updateTour as updateTourRedux } from 'app/services/redux/slices';
import { ShareTourModal } from 'app/components/common';
import { MoveToFolderMenu } from 'app/components/pages/Personal/components/MoveToFolderMenu/MoveToFolderMenu';
import {
  updateTourInfo,
  TourContactInfo,
  getTourStartLocation,
  setTourStartLocation,
  getTourArea,
  setTourArea,
  sendTourRequest,
  type TourRequestAttachment,
  getTourLayerSettings,
  setTourLayerSettings,
  isLayerEnabled,
  type CustomPreset,
  getTourDashboardDefaultLayer,
  setTourDashboardDefaultLayer,
  getTourVideos,
  uploadTourVideo,
  deleteTourVideo,
  MAX_VIDEO_UPLOAD_BYTES,
  type TourVideo,
  uploadGalleryImage,
  deleteGalleryImage,
  MAX_GALLERY_UPLOAD_BYTES,
  getTourFloorplans,
  uploadTourFloorplan,
  setDefaultFloorplan,
  renameTourFloorplan,
  deleteTourFloorplan,
  MAX_FLOORPLAN_UPLOAD_BYTES,
  type FloorplanVariant,
  getTourAnalytics,
  type TourAnalytics,
  getCollaborators,
  type CollaboratorInfo,
} from 'app/services/firebase/functions';
import {
  getTourVisibleSpaceVariants,
  setTourVisibleSpaceVariants,
} from 'app/services/firebase/functions/tourSpaceVariants';
import {
  getTourPhotoRows,
  setTourPhotoRows,
  type PhotoRowMap,
} from 'app/services/firebase/functions/tourPhotoRows';
import { generateTourDescription } from 'app/services/ai/generateTourDescription';

import dashboardLogo from 'app/assets/images/dashboard-logo.png';

interface StartLocation {
  // Optional now: the URL-based payload from the viewer's "Set Start
  // Location" mode has no type — buildTourLink uses `savedUrl` verbatim
  // when present and only falls back to type-based param assembly for
  // the legacy dashboard-picker payloads.
  type?: 'pano' | 'floorplan';
  panoId?: string;
  folderPath?: string;
  roomName?: string;
  floorName?: string;
  thumbnailUrl?: string;
  fit?: 'cover' | 'contain';
  url?: string;
  // Full viewer URL captured at Save-click in the viewer. When present,
  // iframe src + share links use this verbatim.
  savedUrl?: string;
}

interface PanoEntry {
  kind: 'pano';
  id: string;
  fullPath: string;
  roomName: string;
  floorName: string;
  thumbnailUrl: string;
}

interface FloorplanEntry {
  kind: 'floorplan';
  id: string;
  floorName: string;
  thumbnailUrl: string | null;
}

const FloorplanIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 32 32"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={classNames('text-black/35', className)}
  >
    <rect x="3" y="3" width="26" height="26" rx="1.5" />
    <path d="M3 14H19M19 3V29M19 11H29M19 22H25" />
  </svg>
);

type StartOption = PanoEntry | FloorplanEntry;

interface TourDetailsJson {
  description?: string;
  contactInfo?: {
    name?: string;
    title?: string;
    email?: string;
    phone?: string;
    photo?: string;
    logo?: string;
    website?: string;
  };
  details?: Array<{
    floor?: string | number;
    floorId?: string;
    floorplan?: {
      id?: string;
      file_key?: string;
      full_file_key?: string;
    };
    model?: {
      id?: string;
      model_key?: string;
      objUrl?: string;
      modelUrl?: string;
      tex_1k_Url?: string;
      tex_2k_Url?: string;
      tex_4k_Url?: string;
      tex_8k_Url?: string;
    };
    rooms?: Array<{
      id?: string;
      name?: string;
      panos?: Array<{
        id?: string;
        config?: {
          equirectangularThumbnail?: string;
        };
      }>;
    }>;
  }>;
}

const STORAGE_BUCKET =
  process.env.REACT_APP_STORAGE_BUCKET || 'id3d-prod.appspot.com';

const resolveFloorplanUrl = (fp: {
  file_key?: string;
  full_file_key?: string;
}): string | null => {
  if (fp.full_file_key) {
    return fp.full_file_key.replace('default-bucket', STORAGE_BUCKET);
  }
  if (fp.file_key) {
    return `https://storage.googleapis.com/${STORAGE_BUCKET}/${fp.file_key}`;
  }
  return null;
};

const extractFloorplans = (
  details: TourDetailsJson | null,
): FloorplanEntry[] => {
  if (!details?.details) return [];
  const out: FloorplanEntry[] = [];
  for (const floor of details.details) {
    const fp = floor?.floorplan;
    const url = fp ? resolveFloorplanUrl(fp) : null;
    out.push({
      kind: 'floorplan',
      id: fp?.id ?? `floor-${floor?.floor ?? 'default'}`,
      floorName: String(floor?.floor ?? ''),
      thumbnailUrl: url,
    });
  }
  return out;
};

const extractPanoList = (details: TourDetailsJson | null): PanoEntry[] => {
  if (!details?.details) return [];
  const out: PanoEntry[] = [];
  for (const floor of details.details) {
    for (const room of floor?.rooms ?? []) {
      for (const pano of room?.panos ?? []) {
        if (!pano.id) continue;
        // The pano's id is the full storage-style path
        // (e.g. tours/<tourId>/rooms/<roomId>/panos/<panoId>). Build the
        // thumbnail URL directly off that — don't rely on the optional
        // config.equirectangularThumbnail field being shaped correctly.
        const thumbnailUrl = `https://storage.googleapis.com/${STORAGE_BUCKET}/${pano.id}/thumbnail_512.jpg`;
        out.push({
          kind: 'pano',
          id: pano.id.split('/').pop() ?? pano.id,
          fullPath: pano.id,
          roomName: room?.name ?? 'Untitled room',
          floorName: String(floor?.floor ?? ''),
          thumbnailUrl,
        });
      }
    }
  }
  return out;
};

const getIframeOrigin = (): string | null => {
  const raw = process.env.REACT_APP_IFRAME_URL;
  if (!raw) return null;
  try {
    return new URL(raw).origin;
  } catch {
    return null;
  }
};
const IFRAME_ORIGIN = getIframeOrigin();

type SectionId =
  | 'overview'
  | '3d-tour'
  | 'pictures'
  | 'video'
  | 'floorplan'
  | 'settings';

const OverviewIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 10.5L12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1v-9.5z" />
  </svg>
);

const TourCubeIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 20 20"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M10 2.5l6.5 3.5v8L10 17.5l-6.5-3.5v-8L10 2.5z" />
    <path d="M3.5 6L10 9.5l6.5-3.5M10 9.5v8" />
  </svg>
);

const PictureIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 20 20"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2.5" y="3.5" width="15" height="13" rx="1.75" />
    <circle cx="7" cy="8" r="1.25" />
    <path d="M2.5 13l4-3.5L11 13l3-2.5 3.5 3" />
  </svg>
);

const VideoIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="7" width="12" height="10" rx="2" />
    <path d="M15 11l5-3v8l-5-3z" />
  </svg>
);

const FloorplanNavIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 20 20"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2.5" y="2.5" width="15" height="15" rx="1.25" />
    <path d="M2.5 9h9M11.5 2.5v15M11.5 8h6M11.5 14h4" />
  </svg>
);

const SettingsIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const SECTIONS: { id: SectionId; label: string; icon: ReactElement }[] = [
  { id: 'overview', label: 'Overview', icon: <OverviewIcon /> },
  { id: '3d-tour', label: '3D Tour', icon: <TourCubeIcon /> },
  { id: 'pictures', label: 'Photography', icon: <PictureIcon /> },
  { id: 'video', label: 'Video', icon: <VideoIcon /> },
  { id: 'floorplan', label: 'Floorplan', icon: <FloorplanNavIcon /> },
  { id: 'settings', label: 'Settings', icon: <SettingsIcon /> },
];

// Space-variant tabs above the Photography gallery. `id` is the app-internal
// key; `label`/`shortLabel` are what the pill shows.
type SpaceVariantId =
  | 'existing'
  | 'empty'
  | 'renovated-a'
  | 'renovated-b'
  | 'renovated-c';
const SPACE_VARIANTS: {
  id: SpaceVariantId;
  label: string;
  shortLabel?: string;
}[] = [
  { id: 'existing', label: 'Actual Space', shortLabel: 'Actual' },
  { id: 'empty', label: 'Empty Space', shortLabel: 'Empty' },
  { id: 'renovated-a', label: 'Renovated A', shortLabel: 'Reno A' },
  { id: 'renovated-b', label: 'Renovated B', shortLabel: 'Reno B' },
  { id: 'renovated-c', label: 'Renovated C', shortLabel: 'Reno C' },
];
// Which gallery-image tags (lowercased) count as photos for each variant.
// A tour's variant tab is only shown when at least one photo carries one of
// these tags, so the aliases must cover every label the upload flow / editor
// might stamp on an image for that space.
const SPACE_VARIANT_TAG_ALIASES: Record<SpaceVariantId, string[]> = {
  existing: ['actual', 'actual space', 'existing', 'existing space'],
  empty: ['empty', 'empty space'],
  'renovated-a': ['renovated a', 'reno a'],
  'renovated-b': ['renovated b', 'reno b'],
  'renovated-c': ['renovated c', 'reno c'],
};

interface StatCardProps {
  label: string;
  value: string | number;
}
const StatCard = ({ label, value }: StatCardProps) => (
  <div className="bg-white rounded-2xl border border-black/[0.06] shadow-[0_2px_8px_rgba(0,0,0,0.04)] px-5 py-4 max-md:px-3 max-md:py-3 min-w-0">
    <div className="text-[11.5px] max-md:text-[10px] font-medium text-black/55 m-0 truncate">
      {label}
    </div>
    <div className="text-[26px] max-md:text-[20px] font-bold tracking-tight text-[#151A30] mt-1.5 leading-none truncate">
      {value}
    </div>
  </div>
);

function formatAvgDuration(
  totalMs: number | undefined,
  sessions: number | undefined,
): string {
  if (!totalMs || !sessions) return '—';
  const avg = totalMs / sessions;
  if (avg < 60_000) return `${Math.round(avg / 1000)}s`;
  if (avg < 60 * 60_000) {
    const m = Math.floor(avg / 60_000);
    const s = Math.round((avg % 60_000) / 1000);
    return s > 0 ? `${m}m ${s}s` : `${m}m`;
  }
  const h = Math.floor(avg / 3_600_000);
  const m = Math.floor((avg % 3_600_000) / 60_000);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

/** Format a Firestore-style timestamp ({ _seconds }) as a readable date,
 *  or '—' when the value is missing. */
function formatTourDate(
  ts: { _seconds: number; _nanoseconds: number } | undefined,
): string {
  if (!ts?._seconds) return '—';
  return new Date(ts._seconds * 1000).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

interface AssetCardProps {
  icon: ReactElement;
  title: string;
  description: string;
  onClick: () => void;
}
const AssetCard = ({ icon, title, description, onClick }: AssetCardProps) => (
  <button
    type="button"
    onClick={onClick}
    className="group bg-white rounded-2xl border border-black/[0.06] shadow-[0_2px_8px_rgba(0,0,0,0.04)] px-4 py-3.5 flex items-center gap-3 text-left transition-all hover:border-black/15 hover:-translate-y-0.5 focus:outline-none cursor-pointer w-full"
  >
    <span className="w-10 h-10 rounded-xl bg-black/[0.05] text-black/70 flex items-center justify-center shrink-0">
      {icon}
    </span>
    <span className="flex-1 min-w-0">
      <span className="block text-[13.5px] font-semibold text-[#151A30] truncate">
        {title}
      </span>
      <span className="block text-[11.5px] text-black/55 leading-snug mt-0.5 line-clamp-2">
        {description}
      </span>
    </span>
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-black/30 shrink-0 group-hover:text-black/60 transition-colors"
    >
      <path d="M6 4l4 4-4 4" />
    </svg>
  </button>
);

const ComingSoonPanel = ({ label }: { label: string }) => (
  <div className="rounded-3xl border border-dashed border-black/15 bg-white p-12 flex flex-col items-center justify-center text-center">
    <div className="w-12 h-12 rounded-full bg-black/[0.05] text-black/70 flex items-center justify-center mb-3">
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </svg>
    </div>
    <h3 className="text-[15px] font-semibold text-black/85 m-0">{label}</h3>
    <p className="text-[12.5px] text-black/55 m-0 mt-1 max-w-sm">
      Coming soon. We&rsquo;re building this section &mdash; check back in a
      future update.
    </p>
  </div>
);

type MediaSection = 'two-d-staging' | 'photography' | 'floorplan';

type ViewerMode = 'view' | 'edit';

const buildTourLink = (
  tour: Tour,
  startLocation: StartLocation | null,
  mode: ViewerMode = 'view',
  layer?: string,
  hideLayers?: boolean,
  noTrack?: boolean,
  flythroughEdit?: boolean,
  startLocationEdit?: boolean,
): string => {
  const base =
    mode === 'edit'
      ? process.env.REACT_APP_STAGER_URL || process.env.REACT_APP_IFRAME_URL
      : process.env.REACT_APP_IFRAME_URL;

  // Fast path: viewer's "Set Start Location" mode captured a full
  // URL. Use it verbatim (it already carries mode/floor/pano/pitch/yaw),
  // then merge dashboard-runtime overrides on top via URLSearchParams so
  // things like the layer picker still take effect. Skips edit mode
  // because the stager needs its own base URL.
  if (mode !== 'edit' && startLocation?.savedUrl) {
    try {
      const savedUrl = new URL(startLocation.savedUrl);
      // Strip runtime-only params before applying overrides. If the
      // viewer captured its URL while in edit mode, `startLocationEdit=1`
      // (or `flythroughEdit=1`, or `noTrack=1`) rides along inside the
      // saved URL — leaving those in would drop the iframe back into
      // edit mode on the next dashboard load without the user asking,
      // creating a "sticky edit mode" bug that only clears when the
      // user manually re-saves from a clean URL.
      savedUrl.searchParams.delete('startLocationEdit');
      savedUrl.searchParams.delete('flythroughEdit');
      savedUrl.searchParams.delete('noTrack');
      // Normalize origin to the canonical public embed host. Historical
      // saves captured `stager-a.the3dapp.com` (the editor build) into
      // the URL — rewrite that on the fly so the iframe loads the
      // embed build regardless of what's in Firestore. Once a fresh
      // save runs through the sanitize path (viewer + cloud fn), the
      // persisted value will already match this and the swap becomes
      // a no-op.
      savedUrl.protocol = 'https:';
      savedUrl.host = 'embed.the3dapp.com';
      if (layer) savedUrl.searchParams.set('layer', layer);
      if (hideLayers) savedUrl.searchParams.set('hideLayers', '1');
      if (noTrack) savedUrl.searchParams.set('noTrack', '1');
      if (flythroughEdit) savedUrl.searchParams.set('flythroughEdit', '1');
      if (startLocationEdit)
        savedUrl.searchParams.set('startLocationEdit', '1');
      const finalUrl = savedUrl.toString();
      console.log('[TourView] iframe URL (savedUrl)', {
        name: tour.name,
        savedUrl: startLocation.savedUrl,
        finalUrl,
      });
      return finalUrl;
    } catch (_e) {
      // Malformed saved URL — fall through to the legacy param builder
      // so the tour still loads at some sane orientation.
      console.warn('[TourView] savedUrl parse failed, falling back:', _e);
    }
  }

  const tourIdParam = getLastId(tour.full_id) ?? 'X';

  const params: { param: string; value: string }[] = [
    { param: 'tour_id', value: tourIdParam },
    { param: 'debug', value: 'false' },
  ];

  if (startLocation?.type === 'floorplan') {
    params.push({ param: 'mode', value: 'floorplan' });
    if (startLocation.floorName) {
      params.push({ param: 'floor', value: startLocation.floorName });
    }
  } else if (startLocation?.type === 'pano' && startLocation.panoId) {
    params.push({ param: 'mode', value: 'walk' });
    if (startLocation.floorName) {
      params.push({ param: 'floor', value: startLocation.floorName });
    }
    params.push({ param: 'pano', value: startLocation.panoId });
  } else {
    params.push({ param: 'mode', value: 'orbit' });
  }

  if (layer) {
    params.push({ param: 'layer', value: layer });
  }

  if (hideLayers) {
    params.push({ param: 'hideLayers', value: '1' });
  }

  if (noTrack) {
    // Tells the viewer to skip analytics calls — used for the dashboard's
    // own iframe so the owner's preview clicks don't inflate view counts.
    params.push({ param: 'noTrack', value: '1' });
  }

  if (flythroughEdit) {
    // Opens the Stager editor straight into flythrough path-drawing mode.
    params.push({ param: 'flythroughEdit', value: '1' });
  }

  if (startLocationEdit) {
    // Opens the viewer straight into Set-Start-Location edit mode. The
    // viewer picks this up in Main.vue's mounted() hook and dispatches
    // startLocation/enter_edit_mode once the tour is ready.
    params.push({ param: 'startLocationEdit', value: '1' });
  }

  const url = addParamsToUrl(base, params);
  console.log('[TourView] iframe URL', {
    name: tour.name,
    base,
    fullIdFromFirestore: tour.full_id,
    tourIdInUrl: tourIdParam,
    startLocation,
    params,
    url,
  });
  return url;
};

interface ToggleProps {
  checked: boolean;
  onChange?: (v: boolean) => void;
  disabled?: boolean;
}
const Toggle = ({ checked, onChange, disabled }: ToggleProps) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => onChange?.(!checked)}
    className={classNames(
      // Pill (track): explicit dimensions, neutralized button chrome.
      'relative inline-block h-6 w-11 shrink-0 rounded-full p-0 m-0 border-0',
      'transition-colors duration-150 outline-none focus:outline-none',
      disabled
        ? 'bg-black/10 cursor-not-allowed'
        : 'cursor-pointer ' + (checked ? 'bg-black/85' : 'bg-black/20'),
    )}
  >
    <span
      // Thumb: positioned with `left` (not translate) so position is
      // unambiguous regardless of inline/flex/positioning quirks on the
      // parent button.
      className={classNames(
        'absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm',
        'transition-[left] duration-150',
        checked ? 'left-[22px]' : 'left-0.5',
      )}
    />
  </button>
);

interface SettingCardProps {
  icon: ReactElement;
  title: string;
  description?: ReactNode;
  control?: ReactNode;
  footer?: ReactNode;
  preview?: ReactNode;
  /** When set, the entire card becomes the clickable region (any tap —
   *  including the title/description text — fires the handler). Caller
   *  must not also wrap `preview` in its own <button> when this is set,
   *  since HTML doesn't allow nested buttons. */
  onClick?: () => void;
  disabled?: boolean;
  ariaLabel?: string;
}
const SettingCard = ({
  icon,
  title,
  description,
  control,
  footer,
  preview,
  onClick,
  disabled,
  ariaLabel,
}: SettingCardProps) => {
  const base =
    'bg-white rounded-[14px] border border-black/[0.06] shadow-[0_6px_18px_rgba(0,0,0,0.10),0_2px_4px_rgba(0,0,0,0.06)] p-3 flex flex-row items-center md:flex-col md:items-stretch w-full text-left';
  const interactive = onClick
    ? 'cursor-pointer hover:bg-black/[0.02] active:bg-black/[0.04] transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
    : '';

  const body = (
    <>
      <div className="relative shrink-0 w-20 h-20 md:w-auto md:h-auto md:aspect-[5/4] bg-black/[0.04] rounded-[10px] overflow-hidden flex items-center justify-center mr-3 md:mr-0 md:mb-3">
        {preview ?? (
          <span className="text-black/80 [&_svg]:!w-6 [&_svg]:!h-6">
            {icon}
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0 flex items-start gap-2 px-1">
        <div className="flex-1 min-w-0">
          <h3 className="text-[13px] font-semibold text-[#151A30] m-0 mb-0.5">
            {title}
          </h3>
          {description && (
            <p className="text-[11.5px] text-black/50 leading-snug m-0">
              {description}
            </p>
          )}
        </div>
        {control && <div className="shrink-0 pt-0.5">{control}</div>}
      </div>
      {footer && <div className="pt-2 mt-auto">{footer}</div>}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={ariaLabel}
        className={classNames(base, interactive, 'border-0 focus:outline-none')}
      >
        {body}
      </button>
    );
  }
  return <div className={base}>{body}</div>;
};

interface GalleryImage {
  id: string;
  url: string;
  uploadedAt?: string;
  caption?: string;
  tags?: string[];
}

interface ModelInfo {
  floorName: string;
  modelUrl: string | null;
  objUrl: string | null;
  textureUrl: string | null;
  pointcloudUrl: string | null;
}

function extractModels(details: TourDetailsJson | null): ModelInfo[] {
  if (!details?.details) return [];
  return details.details
    .filter((f) => f.model)
    .map((f) => {
      return {
        floorName: String(f.floor ?? ''),
        modelUrl: f.model?.model_key
          ? `https://storage.googleapis.com/${STORAGE_BUCKET}/${f.model.model_key}`
          : null,
        objUrl: f.model?.objUrl
          ? `https://storage.googleapis.com/${STORAGE_BUCKET}/${f.model.objUrl}`
          : null,
        textureUrl: f.model?.tex_8k_Url
          ? `https://storage.googleapis.com/${STORAGE_BUCKET}/${f.model.tex_8k_Url}`
          : null,
        pointcloudUrl: f.model?.objUrl
          ? `https://storage.googleapis.com/${STORAGE_BUCKET}/${f.model.objUrl.replace(
              /model\.obj$/,
              'pointcloud.ply',
            )}`
          : null,
      };
    });
}

function ViewerCarousel({
  urls,
  index,
  onClose,
  onPrev,
  onNext,
}: {
  urls: string[];
  index: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}): ReactElement {
  const hasMany = urls.length > 1;
  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-6 bg-black/75"
      style={{ backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center border-0 cursor-pointer hover:bg-black/80 transition-colors focus:outline-none"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M4 4L12 12M12 4L4 12" />
        </svg>
      </button>
      {hasMany && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onPrev();
          }}
          aria-label="Previous image"
          className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/60 text-white flex items-center justify-center border-0 cursor-pointer hover:bg-black/80 transition-colors focus:outline-none max-md:w-9 max-md:h-9"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M10 12L6 8L10 4" />
          </svg>
        </button>
      )}
      {hasMany && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
          aria-label="Next image"
          className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/60 text-white flex items-center justify-center border-0 cursor-pointer hover:bg-black/80 transition-colors focus:outline-none max-md:w-9 max-md:h-9"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M6 4L10 8L6 12" />
          </svg>
        </button>
      )}
      <div
        className="flex flex-col items-center gap-3 max-w-[90vw] max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={urls[index]}
          alt=""
          className="block w-auto h-auto max-w-[90vw] max-h-[calc(90vh-2.5rem)] object-contain select-none"
          draggable={false}
        />
        {hasMany && (
          <span className="text-white/85 text-[12px] font-medium pointer-events-none">
            {index + 1} / {urls.length}
          </span>
        )}
      </div>
    </div>
  );
}

function MediaGallery({
  tour,
  panoThumbnails,
  floorplans,
  tourDetails,
  section,
  filterTag,
  showEmptyPlaceholder,
  flatten,
  title,
  devMode,
  refreshKey,
  onMutated,
  onRequest,
  emptyHeading = 'Please wait, we are generating your new renovation images',
  emptyBody = 'This can take a little while. You can also request a new design and we’ll get started on it.',
  requestLabel = 'Request',
}: {
  tour: Tour;
  panoThumbnails: PanoEntry[];
  floorplans: FloorplanEntry[];
  tourDetails: TourDetailsJson | null;
  section: MediaSection;
  filterTag?: string;
  /** Force a flat grid even when images have tags (skips the per-tag
   *  subheader rendering). Tag pills still overlay each thumbnail. */
  flatten?: boolean;
  /** Override the section heading text. Defaults to "Gallery". */
  title?: string;
  showEmptyPlaceholder?: boolean;
  /** When true, exposes Upload Image + delete affordances on each row.
   *  Driven by the per-tour Developer Mode toggle in the Settings tab. */
  devMode?: boolean;
  /** Bumped by the parent after a successful upload/delete so every
   *  MediaGallery instance for the same tour refetches gallery.json. */
  refreshKey?: number;
  /** Fires after a successful upload or delete inside this instance so
   *  the parent can bump every other gallery's refreshKey. */
  onMutated?: () => void;
  /** Opens the request modal from the empty-state CTA when no images
   *  have been generated yet. */
  onRequest?: () => void;
  /** Empty-state copy + CTA label, overridable per section (e.g. the
   *  photography tab shows photo-specific wording). */
  emptyHeading?: string;
  emptyBody?: string;
  requestLabel?: string;
}): ReactElement {
  const tourId = getLastId(tour.full_id) ?? '';
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewer, setViewer] = useState<{
    urls: string[];
    index: number;
  } | null>(null);
  const openViewer = (urls: string[], index: number) =>
    setViewer({ urls, index });
  const closeViewer = () => setViewer(null);
  const viewerNext = useCallback(() => {
    setViewer((v) => (v ? { ...v, index: (v.index + 1) % v.urls.length } : v));
  }, []);
  const viewerPrev = useCallback(() => {
    setViewer((v) =>
      v ? { ...v, index: (v.index - 1 + v.urls.length) % v.urls.length } : v,
    );
  }, []);

  useEffect(() => {
    if (!viewer) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeViewer();
      else if (e.key === 'ArrowRight') viewerNext();
      else if (e.key === 'ArrowLeft') viewerPrev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [viewer, viewerNext, viewerPrev]);
  const models = useMemo(() => extractModels(tourDetails), [tourDetails]);
  const floorplansWithUrls = floorplans.filter((fp) => fp.thumbnailUrl);

  useEffect(() => {
    if (!tourId) return;
    // Cache-bust: the tour-gallery-api doesn't set no-cache on writes, so
    // tag/caption edits made in the viewer would otherwise stay invisible
    // here until the browser TTL expired.
    const url = `https://storage.googleapis.com/${STORAGE_BUCKET}/tours/${tourId}/gallery.json?_=${Date.now()}`;
    let cancelled = false;
    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (!cancelled) setImages(data.images || []);
      })
      .catch((err) =>
        console.warn('[MediaGallery] gallery.json not found', err),
      )
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tourId, refreshKey]);

  // Optional single-tag filter, driven by the space-variant tabs above the
  // gallery (Existing Space / Empty Space / Renovated A / Renovated B / Renovated C).
  // When set, the grid shows only images whose tags include that label.
  const normalizedFilter = filterTag?.trim().toLowerCase() || null;
  const filteredImages = useMemo(() => {
    if (!normalizedFilter) return images;
    return images.filter((img) =>
      (img.tags ?? []).some(
        (t) =>
          typeof t === 'string' && t.trim().toLowerCase() === normalizedFilter,
      ),
    );
  }, [images, normalizedFilter]);

  // Group gallery images by tag for the "Empty Space / Renovated A / …"
  // sections. Multi-tagged images appear in every matching group; untagged
  // images fall into "Other". Group order = first-appearance order in the
  // image list so user reordering in the editor stays meaningful.
  const taggedGroups = useMemo(() => {
    const groupMap = new Map<string, GalleryImage[]>();
    const untagged: GalleryImage[] = [];
    for (const img of images) {
      const tags = (img.tags ?? []).filter((t) => t && t.trim());
      if (tags.length === 0) {
        untagged.push(img);
        continue;
      }
      for (const tag of tags) {
        const key = tag.trim();
        const bucket = groupMap.get(key);
        if (bucket) bucket.push(img);
        else groupMap.set(key, [img]);
      }
    }
    const groups: { tag: string; images: GalleryImage[] }[] = [];
    groupMap.forEach((imgs, tag) => groups.push({ tag, images: imgs }));
    if (untagged.length) groups.push({ tag: 'Other', images: untagged });
    return groups;
  }, [images]);

  // When a filter is active OR the caller asked for a flat view, render a
  // flat grid (filter already narrowed to one tag, or caller wants every
  // image with tag pills overlaid). Otherwise group by tag.
  const hasAnyTags = useMemo(
    () =>
      !normalizedFilter &&
      !flatten &&
      images.some((img) => (img.tags ?? []).some((t) => t && t.trim())),
    [images, normalizedFilter, flatten],
  );

  const [downloadingGallery, setDownloadingGallery] = useState(false);
  const [downloadingPanos, setDownloadingPanos] = useState(false);

  const downloadAsZip = useCallback(
    async (
      urls: { url: string; name: string }[],
      zipName: string,
      setLoading: (v: boolean) => void,
    ) => {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      setLoading(true);
      try {
        await Promise.all(
          urls.map(async ({ url, name }) => {
            try {
              const res = await fetch(url);
              if (!res.ok) return;
              const blob = await res.blob();
              zip.file(name, blob);
            } catch {
              /* skip failed fetches */
            }
          }),
        );
        const content = await zip.generateAsync({ type: 'blob' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(content);
        a.download = zipName;
        a.click();
        URL.revokeObjectURL(a.href);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  // Developer-Mode upload / delete handlers. Mutations call out to the
  // tour-gallery-api wrappers, then bump the parent's refreshKey via
  // onMutated so every MediaGallery instance for this tour refetches.
  // null when idle; otherwise how far through the selected batch we are.
  const [uploadProgress, setUploadProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);
  const uploading = uploadProgress !== null;
  // Uploads run one at a time — each save rewrites gallery.json server-side,
  // so parallel saves could overwrite each other's entries.
  const handleUploadGalleryFiles = async (files: File[]) => {
    if (files.length === 0) return;
    const failures: string[] = [];
    setUploadProgress({ done: 0, total: files.length });
    for (let i = 0; i < files.length; i++) {
      try {
        await uploadGalleryImage(tour.full_id, files[i], {
          tags: filterTag ? [filterTag] : undefined,
        });
      } catch (err) {
        console.warn('[MediaGallery] upload failed', files[i].name, err);
        const detail = err instanceof Error ? err.message : 'Upload failed';
        failures.push(`${files[i].name}: ${detail}`);
      }
      setUploadProgress({ done: i + 1, total: files.length });
    }
    setUploadProgress(null);
    const succeeded = files.length - failures.length;
    if (succeeded > 0) onMutated?.();
    if (failures.length === 0) {
      message.success(
        succeeded === 1 ? 'Image uploaded' : `${succeeded} images uploaded`,
      );
    } else {
      message.error(
        `${failures.length} of ${files.length} failed — ${failures[0]}`,
      );
    }
  };
  const handleDeleteGalleryImage = async (imageId: string) => {
    try {
      await deleteGalleryImage(tour.full_id, imageId);
      onMutated?.();
      message.success('Image deleted');
    } catch (err) {
      console.warn('[MediaGallery] delete failed', err);
      const detail = err instanceof Error ? err.message : 'Delete failed';
      message.error(detail);
    }
  };

  const thumbButton = (
    url: string,
    key: string,
    tags: string[] | undefined,
    urls: string[],
    index: number,
    imageId?: string,
  ) => {
    // Hide the active variant tag (e.g. "Renovated A") on each thumbnail —
    // the variant tab above already communicates which variant is shown.
    const visibleTags = (tags ?? [])
      .map((t) => (typeof t === 'string' ? t.trim() : ''))
      .filter(Boolean)
      .filter((t) => !normalizedFilter || t.toLowerCase() !== normalizedFilter);
    return (
      <div key={key} className="relative group">
        <button
          type="button"
          onClick={() => openViewer(urls, index)}
          className="relative aspect-[4/3] rounded-xl overflow-hidden bg-black/5 border border-black/10 hover:border-black/30 transition-all hover:-translate-y-0.5 cursor-pointer focus:outline-none w-full p-0"
        >
          <img
            src={url}
            alt=""
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
          />
          {visibleTags.length > 0 && (
            <div className="absolute top-2 left-2 right-2 flex flex-wrap items-center gap-1.5 pointer-events-none">
              {visibleTags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center px-2.5 py-1 rounded-[14px] text-[12px] font-medium leading-[1.2] whitespace-nowrap text-white/[0.92] bg-white/[0.18] border border-white/[0.25] backdrop-blur-md"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </button>
        {devMode && imageId && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteGalleryImage(imageId);
            }}
            aria-label="Delete image"
            className="absolute top-2 right-2 inline-flex items-center justify-center w-7 h-7 rounded-full bg-black/65 hover:bg-black/85 text-white border-0 cursor-pointer focus:outline-none opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <svg
              width="11"
              height="11"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <path d="M4 4l8 8M12 4l-8 8" />
            </svg>
          </button>
        )}
      </div>
    );
  };

  return (
    <div>
      {/* Gallery images */}
      {section === 'two-d-staging' &&
        (loading ||
          filteredImages.length > 0 ||
          (showEmptyPlaceholder && (!!filterTag || flatten))) && (
          <section className="mb-5 rounded-[20px] border border-black/10 bg-white shadow-sm overflow-hidden">
            <header className="px-6 py-4 border-b border-black/[0.08] flex items-center justify-between gap-3">
              <h2 className="text-[15px] font-semibold text-black/85 m-0">
                {title ?? 'Thumbnails'}
              </h2>
              <div className="flex items-center gap-2">
                {devMode && (
                  <label className="relative btn-chip text-[11px] inline-flex items-center gap-1.5 cursor-pointer">
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M8 14V6M4.5 9L8 5.5 11.5 9M3 3h10" />
                    </svg>
                    {uploadProgress
                      ? `Uploading ${Math.min(
                          uploadProgress.done + 1,
                          uploadProgress.total,
                        )}/${uploadProgress.total}…`
                      : 'Upload Images'}
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      className="sr-only"
                      disabled={uploading}
                      onChange={(e) => {
                        const files = Array.from(e.target.files ?? []);
                        e.target.value = '';
                        handleUploadGalleryFiles(files);
                      }}
                    />
                  </label>
                )}
              </div>
            </header>
            <div className="p-6">
              {loading ? (
                <div className="grid grid-cols-3 gap-3 max-md:grid-cols-1 max-md:gap-4">
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <div
                      key={i}
                      className="aspect-[4/3] rounded-xl bg-black/10 animate-pulse"
                    />
                  ))}
                </div>
              ) : filteredImages.length === 0 ? (
                <div className="flex flex-col items-center justify-center text-center py-14 px-6 rounded-2xl bg-black/[0.025] border border-dashed border-black/10">
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="text-black/35 mb-3"
                  >
                    <path d="M10 1l1.5 5.5L17 8l-5.5 1.5L10 15l-1.5-5.5L3 8l5.5-1.5L10 1zM15.5 13l.75 2.25L18.5 16l-2.25.75L15.5 19l-.75-2.25L12.5 16l2.25-.75L15.5 13z" />
                  </svg>
                  <h3 className="text-[14px] font-semibold text-black/80 m-0">
                    {emptyHeading}
                  </h3>
                  <p className="text-[12.5px] text-black/55 mt-1.5 m-0 max-w-sm">
                    {emptyBody}
                  </p>
                  {onRequest && (
                    <button
                      type="button"
                      onClick={onRequest}
                      className="inline-flex items-center justify-center gap-1.5 h-9 px-4 mt-4 text-[13px] rounded-full leading-none whitespace-nowrap font-semibold text-white bg-black/85 hover:bg-black transition-colors border-0 cursor-pointer focus:outline-none"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path d="M10 1l1.5 5.5L17 8l-5.5 1.5L10 15l-1.5-5.5L3 8l5.5-1.5L10 1zM15.5 13l.75 2.25L18.5 16l-2.25.75L15.5 19l-.75-2.25L12.5 16l2.25-.75L15.5 13z" />
                      </svg>
                      {requestLabel}
                    </button>
                  )}
                </div>
              ) : hasAnyTags ? (
                <div className="flex flex-col gap-6">
                  {taggedGroups.map((group) => {
                    const groupUrls = group.images.map((img) => img.url);
                    return (
                      <div key={group.tag}>
                        <div className="flex items-center gap-2 mb-3">
                          <h3 className="text-[13px] font-semibold text-black/75 m-0">
                            {group.tag}
                          </h3>
                          <span className="text-[11px] text-black/45">
                            {group.images.length}
                          </span>
                        </div>
                        <div className="grid grid-cols-3 gap-3 max-md:grid-cols-1 max-md:gap-4">
                          {group.images.map((img, i) =>
                            thumbButton(
                              img.url,
                              `${group.tag}-${img.id}`,
                              img.tags,
                              groupUrls,
                              i,
                              img.id,
                            ),
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-3 max-md:grid-cols-1 max-md:gap-4">
                  {(() => {
                    const urls = filteredImages.map((img) => img.url);
                    return filteredImages.map((img, i) =>
                      thumbButton(img.url, img.id, img.tags, urls, i, img.id),
                    );
                  })()}
                </div>
              )}
            </div>
            {filteredImages.length > 0 && (
              <footer className="px-6 py-3 border-t border-black/[0.06] flex justify-end">
                <button
                  type="button"
                  disabled={downloadingGallery}
                  onClick={() =>
                    downloadAsZip(
                      filteredImages.map((img, i) => ({
                        url: img.url,
                        name: `gallery-${i + 1}.jpg`,
                      })),
                      'gallery.zip',
                      setDownloadingGallery,
                    )
                  }
                  className="btn-chip text-[11px] inline-flex items-center gap-1.5"
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M8 2v8M4.5 7L8 10.5 11.5 7M3 13h10" />
                  </svg>
                  {downloadingGallery ? 'Zipping…' : 'Download'}
                </button>
              </footer>
            )}
          </section>
        )}

      {/* Panorama thumbnails */}
      {section === 'photography' &&
        (tourDetails === null || panoThumbnails.length > 0) && (
          <section className="mb-5 rounded-[20px] border border-black/10 bg-white shadow-sm overflow-hidden">
            <header className="px-6 py-4 border-b border-black/[0.08] flex items-center justify-between">
              <h2 className="text-[15px] font-semibold text-black/85 m-0">
                Thumbnails
              </h2>
            </header>
            <div className="p-6">
              {tourDetails === null ? (
                <div className="grid grid-cols-3 gap-3 max-md:grid-cols-1 max-md:gap-4">
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <div
                      key={i}
                      className="aspect-[4/3] rounded-xl bg-black/10 animate-pulse"
                    />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-3 max-md:grid-cols-1 max-md:gap-4">
                  {(() => {
                    const urls = panoThumbnails.map((p) => p.thumbnailUrl);
                    return panoThumbnails.map((p, i) =>
                      thumbButton(
                        p.thumbnailUrl,
                        p.fullPath,
                        undefined,
                        urls,
                        i,
                      ),
                    );
                  })()}
                </div>
              )}
            </div>
            {panoThumbnails.length > 0 && (
              <footer className="px-6 py-3 border-t border-black/[0.06] flex justify-end">
                <button
                  type="button"
                  disabled={downloadingPanos}
                  onClick={() =>
                    downloadAsZip(
                      panoThumbnails.map((p, i) => ({
                        url: p.thumbnailUrl,
                        name: `pano-${i + 1}.jpg`,
                      })),
                      'panoramas.zip',
                      setDownloadingPanos,
                    )
                  }
                  className="btn-chip text-[11px] inline-flex items-center gap-1.5"
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M8 2v8M4.5 7L8 10.5 11.5 7M3 13h10" />
                  </svg>
                  {downloadingPanos ? 'Zipping…' : 'Download'}
                </button>
              </footer>
            )}
          </section>
        )}

      {/* Floorplans */}
      {section === 'floorplan' && floorplansWithUrls.length > 0 && (
        <section className="mb-5 rounded-[20px] border border-black/10 bg-white shadow-sm overflow-hidden">
          <header className="px-6 py-4 border-b border-black/[0.08] flex items-center justify-between">
            <h2 className="text-[15px] font-semibold text-black/85 m-0">
              Floorplans
            </h2>
          </header>
          <div className="p-6">
            <div className="grid grid-cols-3 gap-3 max-md:grid-cols-2">
              {(() => {
                const urls = floorplansWithUrls.map((fp) => fp.thumbnailUrl!);
                return floorplansWithUrls.map((fp, i) => (
                  <button
                    key={fp.id}
                    type="button"
                    onClick={() => openViewer(urls, i)}
                    className="relative aspect-square rounded-xl overflow-hidden bg-[rgb(77,77,77)] border border-black/10 hover:border-black/30 transition-all hover:-translate-y-0.5 cursor-pointer focus:outline-none"
                  >
                    <img
                      src={fp.thumbnailUrl!}
                      alt={`Floor ${fp.floorName}`}
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 w-full h-full object-contain p-4"
                    />
                  </button>
                ));
              })()}
            </div>
          </div>
          <footer className="px-6 py-3 border-t border-black/[0.06] flex justify-end">
            <button
              type="button"
              onClick={() =>
                downloadAsZip(
                  floorplansWithUrls.map((fp, i) => ({
                    url: fp.thumbnailUrl!,
                    name: `floorplan-${fp.floorName || i + 1}.svg`,
                  })),
                  'floorplans.zip',
                  // eslint-disable-next-line @typescript-eslint/no-empty-function
                  () => {},
                )
              }
              className="btn-chip text-[11px] inline-flex items-center gap-1.5"
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M8 2v8M4.5 7L8 10.5 11.5 7M3 13h10" />
              </svg>
              Download
            </button>
          </footer>
        </section>
      )}

      {/* 3D Models */}
      {section === 'floorplan' && models.length > 0 && (
        <section className="mb-5 rounded-[20px] border border-black/10 bg-white shadow-sm overflow-hidden">
          <header className="px-6 py-4 border-b border-black/[0.08]">
            <h2 className="text-[15px] font-semibold text-black/85 m-0">
              3D Models
            </h2>
          </header>
          <div className="p-6 flex flex-col gap-3">
            {models.map((m) => (
              <div
                key={m.floorName}
                className="flex items-center justify-between gap-3 py-2"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-black/5 text-black/60 shrink-0">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M12 2L2 7l10 5 10-5-10-5z" />
                      <path d="M2 17l10 5 10-5" />
                      <path d="M2 12l10 5 10-5" />
                    </svg>
                  </span>
                  <span className="text-[14px] font-medium text-black/85">
                    Floor {m.floorName || '1'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {m.modelUrl && (
                    <a
                      href={m.modelUrl}
                      download
                      className="btn-chip"
                      target="_blank"
                      rel="noreferrer"
                    >
                      .drc
                    </a>
                  )}
                  {m.objUrl && (
                    <a
                      href={m.objUrl}
                      download
                      className="btn-chip"
                      target="_blank"
                      rel="noreferrer"
                    >
                      .obj
                    </a>
                  )}
                  {m.textureUrl && (
                    <a
                      href={m.textureUrl}
                      download
                      className="btn-chip"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Texture
                    </a>
                  )}
                  {m.pointcloudUrl && (
                    <a
                      href={m.pointcloudUrl}
                      download
                      className="btn-chip"
                      target="_blank"
                      rel="noreferrer"
                    >
                      .ply
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {viewer &&
        createPortal(
          <ViewerCarousel
            urls={viewer.urls}
            index={viewer.index}
            onClose={closeViewer}
            onPrev={viewerPrev}
            onNext={viewerNext}
          />,
          document.body,
        )}
    </div>
  );
}

// ── Photography comparison grid ──────────────────────────────────────────
// One row per space: the Actual photo, then the same space in each
// renovated variant. Which variant a photo belongs to comes from its
// variant tag ("Actual", "Renovated A", …); which row it sits in comes from
// a hidden `row:<id>` tag. Photos uploaded before rows existed have no row
// tag and are paired by order (1st Actual ↔ 1st Reno A ↔ …) until a grid
// edit pins them to explicit rows.

const ROW_TAG_PREFIX = 'row:';

const VARIANT_UPLOAD_TAG: Record<SpaceVariantId, string> = {
  existing: 'Actual',
  empty: 'Empty Space',
  'renovated-a': 'Renovated A',
  'renovated-b': 'Renovated B',
  'renovated-c': 'Renovated C',
};

function isRowTag(t: unknown): t is string {
  return typeof t === 'string' && t.startsWith(ROW_TAG_PREFIX);
}

function variantOfImage(img: GalleryImage): SpaceVariantId | null {
  const tags = (img.tags ?? [])
    .map((t) => (typeof t === 'string' ? t.trim().toLowerCase() : ''))
    .filter(Boolean);
  const match = SPACE_VARIANTS.find((v) =>
    tags.some((t) => SPACE_VARIANT_TAG_ALIASES[v.id].includes(t)),
  );
  return match ? match.id : null;
}

/** Row for an image: the photo-rows.json map wins, then a legacy `row:` tag. */
function rowIdOfImage(img: GalleryImage, rowMap: PhotoRowMap): string | null {
  if (rowMap[img.id]) return rowMap[img.id];
  const tag = (img.tags ?? []).find(isRowTag);
  return tag ? tag.slice(ROW_TAG_PREFIX.length) : null;
}

function newRowId(): string {
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

interface PhotoRow {
  key: string;
  /** null for legacy rows paired by order. */
  rowId: string | null;
  /** A cell can hold several photos of the same space (flip-through). */
  cells: Partial<Record<SpaceVariantId, GalleryImage[]>>;
}

function rowImages(row: PhotoRow): GalleryImage[] {
  return SPACE_VARIANTS.flatMap((v) => row.cells[v.id] ?? []);
}

function buildPhotoRows(
  images: GalleryImage[],
  rowMap: PhotoRowMap,
): PhotoRow[] {
  const order = new Map(images.map((img, i) => [img.id, i]));
  const explicit = new Map<string, PhotoRow>();
  const legacy: Partial<Record<SpaceVariantId, GalleryImage[]>> = {};
  images.forEach((img) => {
    const variant = variantOfImage(img);
    if (!variant) return;
    const rowId = rowIdOfImage(img, rowMap);
    if (rowId) {
      let row = explicit.get(rowId);
      if (!row) {
        row = { key: `row-${rowId}`, rowId, cells: {} };
        explicit.set(rowId, row);
      }
      row.cells[variant] = [...(row.cells[variant] ?? []), img];
      return;
    }
    legacy[variant] = [...(legacy[variant] ?? []), img];
  });
  const legacyCount = Math.max(
    0,
    ...Object.values(legacy).map((list) => list?.length ?? 0),
  );
  const rows: PhotoRow[] = [];
  for (let i = 0; i < legacyCount; i++) {
    const cells: PhotoRow['cells'] = {};
    SPACE_VARIANTS.forEach((v) => {
      const img = legacy[v.id]?.[i];
      if (img) cells[v.id] = [img];
    });
    rows.push({ key: `legacy-${i}`, rowId: null, cells });
  }
  explicit.forEach((row) => rows.push(row));
  // Rows keep the position of their earliest photo in the gallery, so
  // pinning a legacy row to an explicit id never moves it.
  const firstIndex = (row: PhotoRow) =>
    Math.min(...rowImages(row).map((img) => order.get(img.id) ?? Infinity));
  return rows.sort((a, b) => firstIndex(a) - firstIndex(b));
}

function fileExtension(url: string): string {
  const m = /\.([a-z0-9]+)$/i.exec(url.split('?')[0]);
  return m ? m[1].toLowerCase() : 'jpg';
}

async function downloadUrlsAsZip(
  files: { url: string; name: string }[],
  zipName: string,
): Promise<void> {
  const JSZip = (await import('jszip')).default;
  const zip = new JSZip();
  await Promise.all(
    files.map(async ({ url, name }) => {
      try {
        const res = await fetch(url);
        if (res.ok) zip.file(name, await res.blob());
      } catch {
        /* skip failed fetches */
      }
    }),
  );
  const content = await zip.generateAsync({ type: 'blob' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(content);
  a.download = zipName;
  a.click();
  URL.revokeObjectURL(a.href);
}

const DownloadGlyph = ({ size = 14 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M8 2v8.5M4.5 7.5 8 11l3.5-3.5M3 14h10" />
  </svg>
);

const outlinePillCls =
  'inline-flex items-center justify-center gap-1.5 h-9 px-4 text-[13px] font-medium rounded-full leading-none whitespace-nowrap text-black/80 bg-white border border-black/15 hover:border-black/30 hover:text-black transition-colors cursor-pointer focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed';

function PhotoComparisonGrid({
  tour,
  images,
  loaded,
  columns,
  devMode,
  stickyTop,
  onMutated,
  onRequest,
}: {
  tour: Tour;
  images: GalleryImage[];
  loaded: boolean;
  /** Columns in display order; the Actual column gets the → after it. */
  columns: { id: SpaceVariantId; label: string }[];
  devMode: boolean;
  /** Desktop sticky offset for the column headers (section header height). */
  stickyTop?: number;
  onMutated: () => void;
  onRequest: () => void;
}): ReactElement {
  // Row layout (imageId → rowId), kept in its own Storage file.
  const [rowMap, setRowMap] = useState<PhotoRowMap>({});
  const [rowMapLoaded, setRowMapLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setRowMapLoaded(false);
    getTourPhotoRows(tour.full_id)
      .then((map) => {
        if (!cancelled) setRowMap(map);
      })
      .catch((err) => {
        console.warn('[PhotoComparisonGrid] getTourPhotoRows failed', err);
      })
      .finally(() => {
        if (!cancelled) setRowMapLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [tour.full_id]);

  const rows = useMemo(() => buildPhotoRows(images, rowMap), [images, rowMap]);
  const visibleRows = devMode
    ? rows
    : rows.filter((row) => columns.some((c) => row.cells[c.id]?.length));
  const ready = loaded && rowMapLoaded;

  // Which photo each multi-photo cell is showing, keyed `${row.key}:${variant}`.
  const [cellIndex, setCellIndex] = useState<Record<string, number>>({});

  const [viewer, setViewer] = useState<{
    urls: string[];
    index: number;
  } | null>(null);
  const viewerNext = useCallback(() => {
    setViewer((v) => (v ? { ...v, index: (v.index + 1) % v.urls.length } : v));
  }, []);
  const viewerPrev = useCallback(() => {
    setViewer((v) =>
      v ? { ...v, index: (v.index - 1 + v.urls.length) % v.urls.length } : v,
    );
  }, []);
  useEffect(() => {
    if (!viewer) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setViewer(null);
      else if (e.key === 'ArrowRight') viewerNext();
      else if (e.key === 'ArrowLeft') viewerPrev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [viewer, viewerNext, viewerPrev]);

  // One hidden picker shared by every "+" slot; the slot that opened it is
  // remembered here. display:none so focusing it can't scroll the page.
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const pendingSlot = useRef<{
    row: PhotoRow | null;
    variant: SpaceVariantId;
  } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [zipping, setZipping] = useState<string | null>(null);

  // Build a row map that pins every order-paired photo (and every photo
  // still relying on a legacy `row:` tag) to an explicit row, so later edits
  // can't shift the pairing of the rows after them. Pure — the caller
  // writes it in a single setTourPhotoRows call. Entries for photos that
  // are no longer in the gallery are dropped.
  const pinnedRowMap = (): {
    map: PhotoRowMap;
    legacyRowIds: Map<string, string>;
  } => {
    const map: PhotoRowMap = {};
    const legacyRowIds = new Map<string, string>();
    rows.forEach((row) => {
      const rowId = row.rowId ?? newRowId();
      if (!row.rowId) legacyRowIds.set(row.key, rowId);
      rowImages(row).forEach((img) => {
        map[img.id] = rowId;
      });
    });
    return { map, legacyRowIds };
  };

  const openPicker = (row: PhotoRow | null, variant: SpaceVariantId) => {
    if (busy) return;
    pendingSlot.current = { row, variant };
    fileInputRef.current?.click();
  };

  const handlePickedFile = async (file: File) => {
    const slot = pendingSlot.current;
    pendingSlot.current = null;
    if (!slot) return;
    const { row, variant } = slot;
    setBusy(`${row?.key ?? 'new'}:${variant}`);
    try {
      const { map, legacyRowIds } = pinnedRowMap();
      const rowId =
        row?.rowId ?? (row && legacyRowIds.get(row.key)) ?? newRowId();
      const uploaded = await uploadGalleryImage(tour.full_id, file, {
        tags: [VARIANT_UPLOAD_TAG[variant]],
      });
      const next = { ...map, [uploaded.id]: rowId };
      await setTourPhotoRows(tour.full_id, next);
      setRowMap(next);
      if (row) {
        // Show the photo just added.
        const count = (row.cells[variant]?.length ?? 0) + 1;
        setCellIndex((prev) => ({
          ...prev,
          [`${row.key}:${variant}`]: count - 1,
        }));
      }
      message.success('Photo added');
    } catch (err) {
      console.warn('[PhotoComparisonGrid] upload failed', err);
      message.error(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setBusy(null);
      onMutated();
    }
  };

  const handleDelete = async (img: GalleryImage) => {
    if (busy) return;
    setBusy(`delete:${img.id}`);
    try {
      // Pin rows first so removing this photo can't re-pair the rows below.
      const { map } = pinnedRowMap();
      delete map[img.id];
      await setTourPhotoRows(tour.full_id, map);
      setRowMap(map);
      await deleteGalleryImage(tour.full_id, img.id);
      message.success('Photo deleted');
    } catch (err) {
      console.warn('[PhotoComparisonGrid] delete failed', err);
      message.error(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setBusy(null);
      onMutated();
    }
  };

  const rowFiles = (row: PhotoRow, rowNumber: number) =>
    columns.flatMap((c) => {
      const imgs = row.cells[c.id] ?? [];
      return imgs.map((img, i) => ({
        url: img.url,
        name: `Space ${rowNumber} - ${c.label}${
          imgs.length > 1 ? ` (${i + 1})` : ''
        }.${fileExtension(img.url)}`,
      }));
    });

  const runZip = async (
    key: string,
    files: { url: string; name: string }[],
    zipName: string,
  ) => {
    if (files.length === 0 || zipping) return;
    setZipping(key);
    try {
      await downloadUrlsAsZip(files, zipName);
    } finally {
      setZipping(null);
    }
  };

  const tourName = (tour.name || 'Tour').replace(/[\\/:*?"<>|]+/g, ' ').trim();
  const allFiles = visibleRows.flatMap((row, i) => rowFiles(row, i + 1));

  const hasArrow = columns[0]?.id === 'existing' && columns.length > 1;
  const gridTemplateColumns = [
    ...columns.map((c, i) =>
      i === 0 && hasArrow ? 'minmax(140px,1fr) 32px' : 'minmax(140px,1fr)',
    ),
    '36px',
  ].join(' ');
  const minWidth = columns.length * 156 + (hasArrow ? 48 : 0) + 76;

  const renderCell = (
    row: PhotoRow | null,
    rowNumber: number,
    c: {
      id: SpaceVariantId;
      label: string;
    },
  ) => {
    const imgs = row?.cells[c.id] ?? [];
    const cellKey = `${row?.key ?? 'new'}:${c.id}`;
    const cellBusy = busy === cellKey;
    if (row && imgs.length > 0) {
      const count = imgs.length;
      const index = Math.min(cellIndex[cellKey] ?? 0, count - 1);
      const img = imgs[index];
      const step = (delta: number) =>
        setCellIndex((prev) => ({
          ...prev,
          [cellKey]: (index + delta + count) % count,
        }));
      // Full-screen viewer walks the whole row (left to right, every photo
      // in each cell), starting on the photo this cell is showing.
      const viewerImages = columns.flatMap((col) => row.cells[col.id] ?? []);
      const chipCls =
        'inline-flex items-center justify-center rounded-full bg-black/60 hover:bg-black/80 text-white border-0 cursor-pointer focus:outline-none transition-opacity';
      return (
        <div key={c.id} className="relative group/cell">
          <button
            type="button"
            onClick={() =>
              setViewer({
                urls: viewerImages.map((i) => i.url),
                index: Math.max(
                  0,
                  viewerImages.findIndex((i) => i.id === img.id),
                ),
              })
            }
            aria-label={`Open ${c.label} photo for space ${rowNumber}`}
            className="relative block w-full aspect-[4/3] rounded-xl overflow-hidden bg-black/[0.06] border-0 p-0 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-black/40"
          >
            <img
              src={img.url}
              alt=""
              loading="lazy"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </button>
          {count > 1 && (
            <>
              <button
                type="button"
                onClick={() => step(-1)}
                aria-label={`Previous ${c.label} photo`}
                className={`${chipCls} absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 opacity-0 group-hover/cell:opacity-100 max-md:opacity-100`}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M10 12 6 8l4-4" />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => step(1)}
                aria-label={`Next ${c.label} photo`}
                className={`${chipCls} absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 opacity-0 group-hover/cell:opacity-100 max-md:opacity-100`}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m6 4 4 4-4 4" />
                </svg>
              </button>
              <span className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full bg-black/60 text-white text-[11px] font-medium leading-[1.4] pointer-events-none">
                {index + 1}/{count}
              </span>
            </>
          )}
          {devMode && (
            <button
              type="button"
              onClick={() => openPicker(row, c.id)}
              disabled={!!busy}
              aria-label={`Add another ${c.label} photo to space ${rowNumber}`}
              title="Add another photo to this cell"
              className={`${chipCls} absolute bottom-2 left-2 w-7 h-7 opacity-0 group-hover/cell:opacity-100 max-md:opacity-100 disabled:cursor-wait`}
            >
              {cellBusy ? (
                <span className="w-3 h-3 rounded-full border-2 border-white/30 border-t-white animate-spin" />
              ) : (
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <path d="M8 3v10M3 8h10" />
                </svg>
              )}
            </button>
          )}
          {devMode && (
            <button
              type="button"
              onClick={() => handleDelete(img)}
              disabled={!!busy}
              aria-label={`Delete ${c.label} photo`}
              className="absolute top-2 right-2 inline-flex items-center justify-center w-7 h-7 rounded-full bg-black/65 hover:bg-black/85 text-white border-0 cursor-pointer focus:outline-none opacity-0 group-hover/cell:opacity-100 max-md:opacity-100 transition-opacity disabled:cursor-wait"
            >
              <svg
                width="11"
                height="11"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <path d="M4 4l8 8M12 4l-8 8" />
              </svg>
            </button>
          )}
        </div>
      );
    }
    if (devMode) {
      return (
        <button
          key={c.id}
          type="button"
          onClick={() => openPicker(row, c.id)}
          disabled={!!busy}
          aria-label={`Add ${c.label} photo${
            row ? ` to space ${rowNumber}` : ' as a new space'
          }`}
          className="flex items-center justify-center w-full aspect-[4/3] rounded-xl border-2 border-dashed border-black/20 bg-transparent text-black/40 hover:border-black/45 hover:text-black/70 transition-colors cursor-pointer focus:outline-none disabled:cursor-wait"
        >
          {cellBusy ? (
            <span className="text-[12px] font-medium">Uploading…</span>
          ) : (
            <svg
              width="22"
              height="22"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            >
              <path d="M8 3v10M3 8h10" />
            </svg>
          )}
        </button>
      );
    }
    return (
      <div
        key={c.id}
        className="w-full aspect-[4/3] rounded-xl bg-black/[0.03]"
        aria-hidden
      />
    );
  };

  const renderArrow = (key: string) => (
    <div key={key} className="flex items-center justify-center text-black/45">
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M4 12h15M13 6l6 6-6 6" />
      </svg>
    </div>
  );

  const renderRow = (row: PhotoRow | null, rowNumber: number) => {
    const rowKey = row?.key ?? 'new-row';
    const files = row ? rowFiles(row, rowNumber) : [];
    const cells: ReactNode[] = [];
    columns.forEach((c, i) => {
      cells.push(renderCell(row, rowNumber, c));
      if (i === 0 && hasArrow) cells.push(renderArrow(`arrow-${c.id}`));
    });
    return (
      <div
        key={rowKey}
        className="group grid items-center gap-x-4 px-3 py-2.5 rounded-2xl hover:bg-black/[0.045] transition-colors"
        style={{ gridTemplateColumns }}
      >
        {cells}
        <div className="flex items-center justify-center">
          {files.length > 0 && (
            <button
              type="button"
              onClick={() =>
                runZip(rowKey, files, `${tourName} - Space ${rowNumber}.zip`)
              }
              disabled={!!zipping}
              aria-label={`Download space ${rowNumber}`}
              title="Download this row"
              className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-transparent hover:bg-black/[0.06] text-black/70 border-0 cursor-pointer focus:outline-none opacity-0 group-hover:opacity-100 focus-visible:opacity-100 max-md:opacity-100 transition-opacity disabled:cursor-wait"
            >
              {zipping === rowKey ? (
                <span className="w-4 h-4 rounded-full border-2 border-black/20 border-t-black/70 animate-spin" />
              ) : (
                <DownloadGlyph size={20} />
              )}
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <h1 className="text-[22px] font-bold text-[#151A30] m-0">
          Photography
        </h1>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={onRequest}
            className="inline-flex items-center justify-center gap-1.5 h-9 px-4 text-[13px] rounded-full leading-none whitespace-nowrap font-semibold text-white bg-black/85 hover:bg-black transition-colors border-0 cursor-pointer focus:outline-none"
          >
            <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
              <path d="M10 1l1.5 5.5L17 8l-5.5 1.5L10 15l-1.5-5.5L3 8l5.5-1.5L10 1zM15.5 13l.75 2.25L18.5 16l-2.25.75L15.5 19l-.75-2.25L12.5 16l2.25-.75L15.5 13z" />
            </svg>
            <span className="md:hidden">Request</span>
            <span className="hidden md:inline">
              Request New Interior Design
            </span>
          </button>
          <button
            type="button"
            onClick={() =>
              runZip('all', allFiles, `${tourName} - Photography.zip`)
            }
            disabled={allFiles.length === 0 || !!zipping}
            className={outlinePillCls}
          >
            {zipping === 'all' ? (
              <span className="w-3.5 h-3.5 rounded-full border-2 border-black/20 border-t-black/70 animate-spin" />
            ) : (
              <DownloadGlyph />
            )}
            {zipping === 'all' ? 'Zipping…' : 'Download All'}
          </button>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) handlePickedFile(file);
        }}
      />

      {ready && visibleRows.length === 0 && !devMode ? (
        <div className="rounded-3xl border border-dashed border-black/15 bg-black/[0.02] p-12 flex flex-col items-center justify-center text-center">
          <h2 className="text-[16px] font-semibold text-black/85 m-0">
            No photos yet
          </h2>
          <p className="text-[12.5px] text-black/55 mt-1.5 m-0 max-w-sm">
            Photos are being uploaded soon.
          </p>
        </div>
      ) : (
        <div className="max-md:overflow-x-auto max-md:-mx-4 max-md:px-4">
          <div style={{ minWidth }}>
            <div
              className="grid items-end gap-x-4 px-3 pb-3 z-20 bg-[#F7F6F4] md:sticky"
              style={{ gridTemplateColumns, top: stickyTop }}
            >
              {columns.flatMap((c, i) => {
                const label = (
                  <div
                    key={c.id}
                    className="text-[15px] font-medium text-black/85 truncate"
                  >
                    {c.id === 'existing' ? 'Actual Space' : c.label}
                  </div>
                );
                return i === 0 && hasArrow
                  ? [label, <div key="arrow-gap" />]
                  : [label];
              })}
              <div />
            </div>
            {!ready ? (
              <div className="flex flex-col gap-1">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="grid gap-x-4 px-3 py-2.5"
                    style={{ gridTemplateColumns }}
                  >
                    {columns.flatMap((c, ci) => {
                      const cell = (
                        <div
                          key={c.id}
                          className="aspect-[4/3] rounded-xl bg-black/[0.07] animate-pulse"
                        />
                      );
                      return ci === 0 && hasArrow
                        ? [cell, <div key="gap" />]
                        : [cell];
                    })}
                    <div />
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                {visibleRows.map((row, i) => renderRow(row, i + 1))}
                {devMode && renderRow(null, visibleRows.length + 1)}
              </div>
            )}
          </div>
        </div>
      )}

      {viewer &&
        createPortal(
          <ViewerCarousel
            urls={viewer.urls}
            index={viewer.index}
            onClose={() => setViewer(null)}
            onPrev={viewerPrev}
            onNext={viewerNext}
          />,
          document.body,
        )}
    </>
  );
}

const inputCls =
  'w-full px-3 h-10 bg-white border border-black/10 rounded-lg text-[14px] text-black/85 placeholder:text-black/40 outline-none focus:border-black/40 transition-colors';
const labelCls =
  'block text-[11px] font-semibold uppercase tracking-wide text-black/55 mb-2';

function TourInfoForm({
  tour,
  tourDetails,
  onInfoSaved,
}: {
  tour: Tour;
  tourDetails: TourDetailsJson | null;
  onInfoSaved?: (updates: Partial<TourDetailsJson>) => void;
}): ReactElement {
  const tourId = getLastId(tour.full_id) ?? '';
  const dispatch = useAppDispatch();

  const defaultContact: TourContactInfo = {
    name: '',
    title: '',
    email: '',
    phone: '',
    photo: '',
    logo: '',
    website: '',
  };

  const [name, setName] = useState(tour.name || '');
  const [address, setAddress] = useState(tour.address || '');
  const [description, setDescription] = useState('');
  const [contact, setContact] = useState<TourContactInfo>(defaultContact);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Sync form fields from tourDetails.json whenever it changes.
  // Unconditional sync is correct here because tourDetails is set exactly
  // once (in the parent's fetch callback), so it won't stomp user edits.
  useEffect(() => {
    if (!tourDetails) return;
    console.log('[TourInfoForm] tourDetails loaded', {
      description: tourDetails.description,
      contactInfo: tourDetails.contactInfo,
      allKeys: Object.keys(tourDetails),
    });
    setDescription(tourDetails.description ?? '');
    setContact((prev) => ({ ...prev, ...(tourDetails.contactInfo ?? {}) }));
  }, [tourDetails]);

  const updateContact = (field: keyof TourContactInfo, value: string) => {
    setContact((prev) => ({ ...prev, [field]: value }));
  };

  const handleGenerateDescription = async () => {
    if (generating) return;
    const trimmedAddress = address.trim();
    if (!trimmedAddress || trimmedAddress === 'not set') {
      message.error('Add a tour address first so the AI has context.');
      return;
    }
    const panos = extractPanoList(tourDetails);
    if (!panos.length) {
      message.error('No panoramas found for this tour yet.');
      return;
    }
    setGenerating(true);
    try {
      const { description: generated } = await generateTourDescription({
        address: trimmedAddress,
        name: name.trim() || undefined,
        panoUrls: panos.map((p) => p.thumbnailUrl),
      });
      setDescription(generated);
      message.success('Description generated — review and save when ready.');
    } catch (err) {
      console.error('[TourInfoForm] AI generate failed', err);
      message.error(
        (err as Error).message || 'Failed to generate description.',
      );
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      message.error('Tour title is required');
      return;
    }
    setSaving(true);
    try {
      const savedContact = {
        ...contact,
        phone: contact.phone.replace(/\D/g, ''),
      };
      await updateTourInfo({
        tourId: `tours/${tourId}`,
        name: name.trim(),
        address: address.trim() || 'not set',
        description,
        contactInfo: savedContact,
      });
      dispatch(
        updateTourRedux({
          tourId: tour.full_id,
          data: {
            name: name.trim(),
            address: address.trim() || 'not set',
          },
        }),
      );
      // Merge saved values into the parent's tourDetails cache so that
      // when this tab unmounts and remounts the form reads fresh data.
      onInfoSaved?.({ description, contactInfo: savedContact });
      message.success('Tour info saved');
    } catch (err) {
      console.error('[TourInfoForm] save failed', err);
      message.error('Failed to save tour info');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mb-5 rounded-[20px] border border-black/10 bg-white shadow-sm overflow-hidden">
      <header className="px-6 py-4 border-b border-black/[0.08]">
        <h2 className="text-[15px] font-semibold text-black/85 m-0">
          Tour Information
        </h2>
      </header>
      <div className="p-6 flex flex-col gap-5">
        <div>
          <label className={labelCls}>Tour Title *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Title"
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls}>Tour Address</label>
          <input
            type="text"
            value={address === 'not set' ? '' : address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="Address"
            className={inputCls}
          />
        </div>
        <div>
          <div className="flex items-center justify-between gap-3 mb-1.5">
            <label className={classNames(labelCls, 'mb-0')}>
              Tour Description
            </label>
            <button
              type="button"
              onClick={handleGenerateDescription}
              disabled={generating || saving}
              className={classNames(
                'inline-flex items-center gap-1.5 h-8 px-3',
                'rounded-[var(--radius-pill)] text-[12px] font-semibold',
                'text-white bg-[var(--ink-5)] hover:bg-[var(--ink)]',
                'shadow-[0_2px_8px_rgba(0,0,0,0.12)]',
                'transition-all focus:outline-none',
                'disabled:opacity-60 disabled:cursor-not-allowed',
              )}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 20 20"
                fill="currentColor"
                className={generating ? 'animate-spin' : ''}
              >
                {generating ? (
                  <path d="M10 2a8 8 0 018 8h-2a6 6 0 00-6-6V2z" />
                ) : (
                  <path d="M10 1l1.5 5.5L17 8l-5.5 1.5L10 15l-1.5-5.5L3 8l5.5-1.5L10 1zM15.5 13l.75 2.25L18.5 16l-2.25.75L15.5 19l-.75-2.25L12.5 16l2.25-.75L15.5 13z" />
                )}
              </svg>
              {generating ? 'Generating…' : 'Generate with AI'}
            </button>
          </div>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Tell us a little bit about your tour..."
            rows={4}
            className="w-full px-3 py-2.5 bg-white border border-black/10 rounded-lg text-[14px] text-black/85 placeholder:text-black/40 outline-none focus:border-black/40 transition-colors resize-y"
          />
        </div>

        <div className="pt-2 border-t border-black/[0.08]">
          <h3 className="text-[14px] font-semibold text-black/85 m-0 mb-1">
            Contact (optional)
          </h3>
          <p className="text-[12px] text-black/50 m-0 mb-4">
            If you want to add contact info then Name and Email or Phone are
            required.
          </p>
          <div className="grid grid-cols-2 gap-4 max-md:grid-cols-1">
            <div>
              <label className={labelCls}>Name</label>
              <input
                type="text"
                value={contact.name}
                onChange={(e) => updateContact('name', e.target.value)}
                placeholder="Add name"
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Title</label>
              <input
                type="text"
                value={contact.title}
                onChange={(e) => updateContact('title', e.target.value)}
                placeholder="Add job title"
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Email</label>
              <input
                type="email"
                value={contact.email}
                onChange={(e) => updateContact('email', e.target.value)}
                placeholder="Add email address"
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Phone</label>
              <input
                type="tel"
                value={contact.phone}
                onChange={(e) => updateContact('phone', e.target.value)}
                placeholder="Add phone number"
                className={inputCls}
              />
            </div>
            <div className="col-span-2 max-md:col-span-1">
              <label className={labelCls}>Logo Link</label>
              <input
                type="url"
                value={contact.website}
                onChange={(e) => updateContact('website', e.target.value)}
                placeholder="Add logo link"
                className={inputCls}
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            className="btn-primary"
            disabled={saving}
            onClick={handleSave}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </section>
  );
}

interface RequestOrderModalProps {
  open: boolean;
  onClose: () => void;
  requestType: string;
  tour: Tour;
}

interface UploadEntry {
  id: string;
  filename: string;
  contentType: string;
  dataUrl: string;
  size: number;
  previewUrl: string;
}

const MAX_REQUEST_ATTACHMENTS = 8;
const MAX_REQUEST_FILE_SIZE = 5 * 1024 * 1024;

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function RequestOrderModal({
  open,
  onClose,
  requestType,
  tour,
}: RequestOrderModalProps): ReactElement {
  const tourId = getLastId(tour.full_id) ?? '';
  const [requestText, setRequestText] = useState('');
  const [uploads, setUploads] = useState<UploadEntry[]>([]);
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [selectedGalleryUrls, setSelectedGalleryUrls] = useState<Set<string>>(
    new Set(),
  );
  const [submitting, setSubmitting] = useState(false);

  // Reset state every time the modal closes so the next open starts fresh.
  useEffect(() => {
    if (!open) {
      setRequestText('');
      setUploads([]);
      setSelectedGalleryUrls(new Set());
      setSubmitting(false);
    }
  }, [open]);

  // Load this tour's gallery once per open. Same anonymous-public URL the
  // MediaGallery component uses — no auth needed and it's already public.
  useEffect(() => {
    if (!open || !tourId) return;
    let cancelled = false;
    setGalleryLoading(true);
    const url = `https://storage.googleapis.com/${STORAGE_BUCKET}/tours/${tourId}/gallery.json?_=${Date.now()}`;
    fetch(url)
      .then((res) => (res.ok ? res.json() : { images: [] }))
      .then((data: { images?: GalleryImage[] }) => {
        if (!cancelled) setGalleryImages(data.images ?? []);
      })
      .catch(() => {
        if (!cancelled) setGalleryImages([]);
      })
      .finally(() => {
        if (!cancelled) setGalleryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, tourId]);

  const totalAttachments = uploads.length + selectedGalleryUrls.size;
  const canSubmit =
    !submitting && (requestText.trim().length > 0 || totalAttachments > 0);

  const handleFiles = async (files: FileList | File[]) => {
    const list = Array.from(files);
    const available = MAX_REQUEST_ATTACHMENTS - totalAttachments;
    if (available <= 0) {
      message.warning('Attachment limit reached');
      return;
    }
    const accepted: UploadEntry[] = [];
    for (const file of list.slice(0, available)) {
      if (!file.type.startsWith('image/')) {
        message.warning(`${file.name}: only images are supported`);
        continue;
      }
      if (file.size > MAX_REQUEST_FILE_SIZE) {
        message.warning(`${file.name}: too large (max 5MB)`);
        continue;
      }
      const dataUrl = await readFileAsDataUrl(file);
      accepted.push({
        id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        filename: file.name,
        contentType: file.type,
        dataUrl,
        size: file.size,
        previewUrl: dataUrl,
      });
    }
    if (accepted.length) setUploads((prev) => [...prev, ...accepted]);
  };

  const removeUpload = (id: string) => {
    setUploads((prev) => prev.filter((u) => u.id !== id));
  };

  const toggleGallery = (url: string) => {
    setSelectedGalleryUrls((prev) => {
      const next = new Set(prev);
      if (next.has(url)) next.delete(url);
      else if (totalAttachments < MAX_REQUEST_ATTACHMENTS) next.add(url);
      else message.warning('Attachment limit reached');
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    const attachments: TourRequestAttachment[] = [
      ...uploads.map((u) => ({
        kind: 'upload' as const,
        filename: u.filename,
        contentType: u.contentType,
        dataUrl: u.dataUrl,
      })),
      ...Array.from(selectedGalleryUrls).map((url) => ({
        kind: 'gallery' as const,
        url,
      })),
    ];
    try {
      const result = await sendTourRequest({
        tourId,
        tourName: tour.name,
        requestType,
        message: requestText.trim(),
        attachments,
      });
      if (result.emailSent) {
        message.success("Request sent — we'll be in touch.");
      } else {
        message.success('Request submitted.');
      }
      onClose();
    } catch (err) {
      console.error('[RequestOrderModal] submit failed', err);
      message.error('Could not send request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      visible={open}
      onCancel={onClose}
      width={720}
      footer={null}
      closable={false}
      centered
      className="glass-modal"
      destroyOnClose
    >
      <div className="px-1">
        <div className="flex items-start justify-between gap-3 mb-1">
          <div className="min-w-0">
            <h2 className="text-[18px] font-bold text-black/90 m-0 truncate">
              {requestType}
            </h2>
            <p className="text-[12.5px] text-black/55 m-0 mt-1">
              Tell us what you need for {tour.name || 'this tour'}. We&rsquo;ll
              respond by email.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="inline-flex items-center justify-center w-8 h-8 rounded-full text-black/55 hover:text-black/85 hover:bg-black/[0.05] transition-colors border-0 bg-transparent cursor-pointer focus:outline-none"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            >
              <path d="M4 4l8 8M12 4l-8 8" />
            </svg>
          </button>
        </div>

        <textarea
          value={requestText}
          onChange={(e) => setRequestText(e.target.value)}
          placeholder="Describe what you'd like — e.g. floors, rooms, references, deadlines…"
          className="mt-4 w-full min-h-[120px] px-3 py-2.5 bg-white border border-black/10 rounded-xl text-[13px] font-medium text-black/80 placeholder:text-black/40 outline-none focus:border-black/40 transition-colors resize-y"
        />

        <div className="mt-5">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-[13px] font-semibold text-black/80 m-0">
              Attachments
            </h3>
            <span className="text-[11px] text-black/45">
              {totalAttachments}/{MAX_REQUEST_ATTACHMENTS}
            </span>
          </div>

          <label className="relative flex items-center justify-center gap-2 h-20 rounded-xl border border-dashed border-black/15 bg-black/[0.02] hover:bg-black/[0.04] text-[12.5px] text-black/65 cursor-pointer transition-colors">
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M8 3v10M3 8h10" />
            </svg>
            <span>
              Click to upload images
              <span className="text-black/40 ml-2">PNG / JPG, ≤ 5 MB each</span>
            </span>
            <input
              type="file"
              multiple
              accept="image/*"
              className="sr-only"
              onChange={(e) => {
                if (e.target.files) handleFiles(e.target.files);
                e.target.value = '';
              }}
            />
          </label>

          {uploads.length > 0 && (
            <div className="grid grid-cols-4 gap-2 mt-3 max-md:grid-cols-3">
              {uploads.map((u) => (
                <div
                  key={u.id}
                  className="relative aspect-square rounded-lg overflow-hidden bg-black/[0.05] border border-black/10"
                >
                  <img
                    src={u.previewUrl}
                    alt={u.filename}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => removeUpload(u.id)}
                    aria-label="Remove"
                    className="absolute top-1.5 right-1.5 inline-flex items-center justify-center w-6 h-6 rounded-full bg-black/65 text-white border-0 cursor-pointer focus:outline-none hover:bg-black/85"
                  >
                    <svg
                      width="10"
                      height="10"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    >
                      <path d="M4 4l8 8M12 4l-8 8" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-5">
          <h3 className="text-[13px] font-semibold text-black/80 m-0 mb-2">
            Or attach from the tour gallery
          </h3>
          {galleryLoading ? (
            <div className="grid grid-cols-4 gap-2 max-md:grid-cols-3">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="aspect-square rounded-lg bg-black/10 animate-pulse"
                />
              ))}
            </div>
          ) : galleryImages.length === 0 ? (
            <p className="text-[12px] text-black/45 m-0">
              This tour doesn&rsquo;t have gallery images yet.
            </p>
          ) : (
            <div className="grid grid-cols-4 gap-2 max-md:grid-cols-3 max-h-[260px] overflow-y-auto pr-1">
              {galleryImages.map((img) => {
                const selected = selectedGalleryUrls.has(img.url);
                return (
                  <button
                    type="button"
                    key={img.id}
                    onClick={() => toggleGallery(img.url)}
                    className={classNames(
                      'relative aspect-square rounded-lg overflow-hidden border-2 transition-all cursor-pointer focus:outline-none p-0',
                      selected
                        ? 'border-black/85 ring-2 ring-black/10'
                        : 'border-black/10 hover:border-black/30',
                    )}
                  >
                    <img
                      src={img.url}
                      alt=""
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    {selected && (
                      <span className="absolute top-1.5 right-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-black text-white">
                        <svg
                          width="10"
                          height="10"
                          viewBox="0 0 16 16"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M3 8l3 3 7-7" />
                        </svg>
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="inline-flex items-center h-10 px-4 rounded-full text-[13px] font-medium text-black/70 bg-transparent hover:bg-black/[0.04] border border-black/10 transition-colors cursor-pointer focus:outline-none disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="inline-flex items-center h-10 px-5 rounded-full text-[13px] font-semibold text-white bg-black/85 hover:bg-black transition-colors border-0 cursor-pointer focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Sending…' : 'Submit request'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

interface ManageLayersModalProps {
  open: boolean;
  /** `didChange` is true iff the user toggled a layer, deleted a preset,
   *  or changed the default layer — anything that affects iframe URL or
   *  what the viewer renders, so the parent should remount the iframe.
   *  `hideLayers` / `defaultLayer` / `customPresets` round-trip the
   *  latest values so the parent can rebuild share + iframe URLs and
   *  resolve labels (e.g. "Defurnished") without a second fetch. */
  onClose: (result: {
    didChange: boolean;
    hideLayers: boolean;
    defaultLayer: string | null;
    customPresets: CustomPreset[];
  }) => void;
  tour: Tour;
  /** Map of slotName → slot metadata (from the furnitureListSlots endpoint)
   *  used to render staging rows with their human displayName instead of
   *  the raw `slot_<timestamp>` key. */
  slotsByName: SlotsByName;
}

interface LayerRowSpec {
  key: string;
  label: string;
  description: string;
  /** Custom-preset rows expose a delete affordance. */
  deletable?: { presetName: string };
}

const BUILT_IN_LAYER_ROWS: LayerRowSpec[] = [
  {
    key: 'original',
    label: 'Original',
    description: 'Source pano tiles — always available, on by default.',
  },
  {
    key: 'unfurnished',
    label: 'Defurnished',
    description: 'Removes movable furniture. Gemini AI.',
  },
  {
    key: 'decluttered',
    label: 'Decluttered',
    description: 'Tidies clutter, keeps the furniture. Gemini AI.',
  },
  {
    key: 'colorized',
    label: 'Colorized',
    description: 'Real-estate-grade color & lighting. Gemini AI.',
  },
];

interface SlotInfo {
  name: string;
  displayName?: string | null;
}
type SlotsByName = Record<string, SlotInfo>;

/**
 * Resolve a `staging-{slotName}` key to a human label, preferring
 * displayName from the slot list and falling back to the raw slot name
 * (which is itself user-typed in the viewer). The `default` slot renders
 * as bare "default" with no "Staging ·" prefix, per the viewer convention.
 */
function formatStagingLabel(
  key: string,
  slotsByName: SlotsByName | undefined,
): string {
  const slotName = key.startsWith('staging-')
    ? key.slice('staging-'.length)
    : key;
  if (slotName === 'default') return 'default';
  const slot = slotsByName?.[slotName];
  const display =
    (slot?.displayName && slot.displayName.trim()) || slot?.name || slotName;
  return `Staging · ${display}`;
}

/**
 * Fetch the per-tour furniture-slot metadata. CORS-open, unauthenticated.
 * Returns a {slotName → SlotInfo} map for O(1) label lookup. Failures
 * resolve to an empty map — staging rows then fall through to slot name.
 */
async function fetchSlotsByName(tourFullId: string): Promise<SlotsByName> {
  const id = tourFullId.startsWith('tours/') ? tourFullId.slice(6) : tourFullId;
  const url = `https://us-central1-id3d-prod.cloudfunctions.net/furnitureListSlots?tourId=${encodeURIComponent(
    id,
  )}`;
  try {
    const res = await fetch(url);
    if (!res.ok) return {};
    const data = (await res.json()) as { slots?: SlotInfo[] };
    const out: SlotsByName = {};
    for (const slot of data.slots ?? []) {
      if (slot && typeof slot.name === 'string') {
        out[slot.name] = slot;
      }
    }
    return out;
  } catch {
    return {};
  }
}

/**
 * Human label for any layer key. Used both inside the Manage Layers list
 * and in the Share modal info chips so a `defaultLayer = "unfurnished"`
 * surfaces as "Defurnished" everywhere.
 */
function labelForLayerKey(
  key: string | null | undefined,
  customPresets: CustomPreset[],
  slotsByName?: SlotsByName,
): string {
  if (!key || key === 'original') return 'Original';
  const builtIn = BUILT_IN_LAYER_ROWS.find((r) => r.key === key);
  if (builtIn) return builtIn.label;
  if (key.startsWith('staging-')) return formatStagingLabel(key, slotsByName);
  if (key.startsWith('custom-')) {
    const name = key.slice('custom-'.length);
    const preset = customPresets.find((p) => p.name === name);
    return preset?.name ?? name;
  }
  return key;
}

function ManageLayersModal({
  open,
  onClose,
  tour,
  slotsByName,
}: ManageLayersModalProps): ReactElement {
  const [loading, setLoading] = useState(false);
  const [enabledLayers, setEnabledLayers] = useState<Record<string, boolean>>(
    {},
  );
  const [customPresets, setCustomPresets] = useState<CustomPreset[]>([]);
  const [hideLayers, setHideLayers] = useState(false);
  // null / "" / "original" all mean "no default" (viewer opens with the
  // original tiles, no &layer= param emitted).
  const [defaultLayer, setDefaultLayer] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Tracks whether the user altered visibility or deleted a preset during
  // this open session — so the parent only remounts the viewer iframe when
  // something actually changed. Reset to false on every open.
  const dirtyRef = useRef(false);
  const handleClose = () =>
    onClose({
      didChange: dirtyRef.current,
      hideLayers,
      defaultLayer,
      customPresets,
    });

  useEffect(() => {
    if (open) dirtyRef.current = false;
  }, [open]);

  // Hydrate from cloud each time the modal opens, with a cache-buster on
  // the GCS URL (built into getTourLayerSettings) so edits made in the
  // viewer surface here immediately.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    // Fetch layer-settings.json (enabled layers + custom presets + hideLayers)
    // and the dashboard-only defaultLayer (stored on the tour Firestore doc
    // via collaborators-api) in parallel, so the modal opens with both in
    // hand. Failure on either side is non-fatal — we surface a load banner
    // for the JSON path and silently fall back to null for the default layer.
    Promise.all([
      getTourLayerSettings(tour.full_id).catch((err) => {
        console.warn('[ManageLayers] layer-settings load failed', err);
        if (!cancelled) setLoadError('Could not load layer settings.');
        return null;
      }),
      getTourDashboardDefaultLayer(tour.full_id).catch((err) => {
        console.warn('[ManageLayers] defaultLayer load failed', err);
        return null;
      }),
    ])
      .then(([settings, savedDefault]) => {
        if (cancelled) return;
        setEnabledLayers(settings?.enabledLayers ?? {});
        setCustomPresets(settings?.customPresets ?? []);
        setHideLayers(!!settings?.hideLayers);
        setDefaultLayer(savedDefault ?? null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, tour.full_id]);

  // Debounced visibility-only write. Rapid toggling collapses to one POST.
  // Preset CRUD writes are immediate (further down) so the user gets the
  // failure feedback synchronously.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const queueLayersSave = (next: Record<string, boolean>) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setTourLayerSettings(tour.full_id, { enabledLayers: next }).catch(
        (err) => {
          console.warn('[ManageLayers] save failed', err);
          message.warning('Could not save layer changes');
        },
      );
    }, 500);
  };
  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  const toggleLayer = (key: string, value: boolean) => {
    const next = { ...enabledLayers, [key]: value };
    setEnabledLayers(next);
    queueLayersSave(next);
    dirtyRef.current = true;
  };

  // Both hideLayers and defaultLayer affect the dashboard's iframe URL now
  // (hideLayers → &hideLayers=1, defaultLayer → &layer=…), so changes here
  // mark dirtyRef so the parent remounts the iframe on close. Saves are
  // immediate — these are single-value edits, no debounce payoff.
  const toggleHideLayers = async (value: boolean) => {
    setHideLayers(value);
    dirtyRef.current = true;
    try {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
        await setTourLayerSettings(tour.full_id, {
          enabledLayers,
          hideLayers: value,
        });
      } else {
        await setTourLayerSettings(tour.full_id, { hideLayers: value });
      }
    } catch (err) {
      console.warn('[ManageLayers] hideLayers save failed', err);
    }
  };

  const updateDefaultLayer = async (value: string | null) => {
    // Normalize "original" / "" to null so the persisted shape stays clean.
    const normalized = value && value !== 'original' ? value : null;
    setDefaultLayer(normalized);
    dirtyRef.current = true;
    try {
      await setTourDashboardDefaultLayer(tour.full_id, normalized);
    } catch (err) {
      console.warn('[ManageLayers] defaultLayer save failed', err);
    }
  };

  // Surface any staging-{slotName} keys the viewer has registered for this
  // tour. We don't add them ourselves; they only appear here if the viewer
  // has already created the staging slot.
  const stagingRows = useMemo<LayerRowSpec[]>(() => {
    return Object.keys(enabledLayers)
      .filter((k) => k.startsWith('staging-'))
      .sort()
      .map((k) => ({
        key: k,
        label: formatStagingLabel(k, slotsByName),
        description: 'Per-slot HD-pano render output.',
      }));
  }, [enabledLayers, slotsByName]);

  const presetRows = useMemo<LayerRowSpec[]>(
    () =>
      customPresets.map((p) => ({
        key: `custom-${p.name}`,
        label: p.name,
        description: p.prompt,
        deletable: { presetName: p.name },
      })),
    [customPresets],
  );

  const allRows = [...BUILT_IN_LAYER_ROWS, ...stagingRows, ...presetRows];

  const handleDeletePreset = async (presetName: string) => {
    const layerKey = `custom-${presetName}`;
    const nextPresets = customPresets.filter((p) => p.name !== presetName);
    const nextLayers = { ...enabledLayers };
    delete nextLayers[layerKey];
    try {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      await setTourLayerSettings(tour.full_id, {
        customPresets: nextPresets,
        enabledLayers: nextLayers,
      });
      setCustomPresets(nextPresets);
      setEnabledLayers(nextLayers);
      dirtyRef.current = true;
      message.success(`Removed variant "${presetName}"`);
    } catch (err) {
      console.warn('[ManageLayers] delete failed', err);
      message.error('Could not delete variant');
    }
  };

  return (
    <Modal
      visible={open}
      onCancel={handleClose}
      width={620}
      footer={null}
      closable={false}
      centered
      className="glass-modal"
      destroyOnClose
    >
      <div className="px-1">
        <div className="flex items-start justify-between gap-3 mb-1">
          <div className="min-w-0">
            <h2 className="text-[18px] font-bold text-black/90 m-0 truncate">
              Manage Layers
            </h2>
            <p className="text-[12.5px] text-black/55 m-0 mt-1">
              Control which layer pills appear in the viewer&rsquo;s bottom-left
              selector. Saved per tour.
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            className="inline-flex items-center justify-center w-8 h-8 rounded-full text-black/55 hover:text-black/85 hover:bg-black/[0.05] transition-colors border-0 bg-transparent cursor-pointer focus:outline-none"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            >
              <path d="M4 4l8 8M12 4l-8 8" />
            </svg>
          </button>
        </div>

        {loadError && (
          <div className="mt-4 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-[12px] text-red-700">
            {loadError}
          </div>
        )}

        <div className="mt-5 rounded-2xl border border-black/[0.06] bg-white overflow-hidden">
          {loading ? (
            <div className="flex flex-col divide-y divide-black/[0.06]">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="flex items-center justify-between gap-3 px-4 py-3"
                >
                  <div className="flex flex-col gap-1.5 flex-1">
                    <div className="h-3.5 w-32 rounded bg-black/10 animate-pulse" />
                    <div className="h-3 w-56 rounded bg-black/[0.07] animate-pulse" />
                  </div>
                  <div className="h-6 w-11 rounded-full bg-black/10 animate-pulse" />
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col divide-y divide-black/[0.06]">
              {allRows.map((row) => (
                <div
                  key={row.key}
                  className="flex items-center justify-between gap-3 px-4 py-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-semibold text-black/85 truncate">
                      {row.label}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {row.deletable && (
                      <button
                        type="button"
                        onClick={() =>
                          handleDeletePreset(row.deletable!.presetName)
                        }
                        aria-label={`Delete ${row.label}`}
                        className="inline-flex items-center justify-center w-7 h-7 rounded-md text-black/45 hover:text-red-600 hover:bg-red-50 transition-colors border-0 bg-transparent cursor-pointer focus:outline-none"
                      >
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 16 16"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M3 4h10M6 4V2.5h4V4M5 4l.5 9h5l.5-9" />
                        </svg>
                      </button>
                    )}
                    <Toggle
                      checked={isLayerEnabled(enabledLayers, row.key)}
                      onChange={(v) => toggleLayer(row.key, v)}
                    />
                  </div>
                </div>
              ))}
              {allRows.length === 0 && (
                <div className="px-4 py-6 text-center text-[12.5px] text-black/50">
                  No layers configured for this tour yet.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-5 flex items-center justify-between gap-3 px-4 py-3 rounded-2xl border border-black/[0.06] bg-white">
          <div className="min-w-0">
            <div className="text-[13px] font-semibold text-black/85">
              Default layer
            </div>
            <div className="text-[11.5px] text-black/55">
              Which layer the viewer opens with.
            </div>
          </div>
          <select
            value={defaultLayer ?? 'original'}
            onChange={(e) => updateDefaultLayer(e.target.value)}
            className="h-9 px-3 pr-8 rounded-lg border border-black/15 bg-white text-[13px] font-medium text-black/85 outline-none focus:border-black/40 cursor-pointer"
          >
            <option value="original">Original</option>
            {BUILT_IN_LAYER_ROWS.filter((r) => r.key !== 'original').map(
              (r) => (
                <option key={r.key} value={r.key}>
                  {r.label}
                </option>
              ),
            )}
            {stagingRows.map((r) => (
              <option key={r.key} value={r.key}>
                {r.label}
              </option>
            ))}
            {customPresets.map((p) => (
              <option key={`custom-${p.name}`} value={`custom-${p.name}`}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        <label className="mt-3 flex items-center justify-between gap-3 px-4 py-3 rounded-2xl border border-black/[0.06] bg-white cursor-pointer select-none">
          <div className="min-w-0">
            <div className="text-[13px] font-semibold text-black/85">
              Hide layer selector in shared viewer
            </div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={hideLayers}
            onClick={() => toggleHideLayers(!hideLayers)}
            className={`relative inline-block h-6 w-11 shrink-0 rounded-full p-0 m-0 border-0 transition-colors duration-150 outline-none focus:outline-none cursor-pointer ${
              hideLayers ? 'bg-black/85' : 'bg-black/20'
            }`}
          >
            <span
              className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-[left] duration-150 ${
                hideLayers ? 'left-[22px]' : 'left-0.5'
              }`}
            />
          </button>
        </label>

        <div className="mt-5 flex items-center justify-end">
          <button
            type="button"
            onClick={handleClose}
            className="inline-flex items-center h-10 px-5 rounded-full text-[13px] font-semibold text-white bg-black/85 hover:bg-black transition-colors border-0 cursor-pointer focus:outline-none"
          >
            Done
          </button>
        </div>
      </div>
    </Modal>
  );
}

interface Props {
  tour: Tour;
  onBack: () => void;
}

function TourView({ tour, onBack }: Props): ReactElement {
  const [activeSection, setActiveSection] = useState<SectionId>('overview');
  const sectionRefs = useRef<Partial<Record<SectionId, HTMLElement | null>>>(
    {},
  );
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement | null>(null);
  const [startLocation, setStartLocation] = useState<StartLocation | null>(
    null,
  );
  const [tourDetails, setTourDetails] = useState<TourDetailsJson | null>(null);
  const [changeStartOpen, setChangeStartOpen] = useState(false);
  const [manageLayersOpen, setManageLayersOpen] = useState(false);
  // Developer Mode: session-local (resets on reload + tour switch). When on,
  // the gallery and video sections expose upload + delete affordances.
  const [devMode, setDevMode] = useState(false);
  // Bumped on every gallery upload/delete so all MediaGallery instances for
  // this tour refetch in lockstep.
  const [galleryRefreshKey, setGalleryRefreshKey] = useState(0);
  const bumpGallery = useCallback(() => setGalleryRefreshKey((k) => k + 1), []);
  // Dashboard-uploaded floorplan variants. Independent of the v1-managed
  // tourDetails floorplans; when this list is non-empty it supersedes the
  // legacy per-floor layouts in the Floorplan Manager preview.
  const [floorplanVariants, setFloorplanVariants] = useState<
    FloorplanVariant[]
  >([]);
  const [defaultFloorplanId, setDefaultFloorplanId] = useState<string | null>(
    null,
  );
  const [floorplansLoading, setFloorplansLoading] = useState(true);
  const [uploadingFloorplan, setUploadingFloorplan] = useState(false);
  const [floorplanUploadPct, setFloorplanUploadPct] = useState<number | null>(
    null,
  );
  // When set, the matching variant row renders an inline rename input.
  const [renamingVariant, setRenamingVariant] = useState<{
    id: string;
    draft: string;
  } | null>(null);

  // Tour videos — fetched on tour open and refetched after upload/delete.
  // Per-tour analytics (views / sessions / shares) for the Overview stat
  // cards. Fetched on tour open; null while loading.
  const [analytics, setAnalytics] = useState<TourAnalytics | null>(null);

  // The tour's original owner — surfaced as "Who captured it" in Settings.
  // Resolved via the collaborators-api `list` action on tour open.
  const [tourOwner, setTourOwner] = useState<CollaboratorInfo | null>(null);

  const [videos, setVideos] = useState<TourVideo[]>([]);
  const [videosLoading, setVideosLoading] = useState(true);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  // Whole-number progress percent shown on the upload button label. null
  // before bytes start flowing so we don't briefly render "Uploading 0%…".
  const [videoUploadPct, setVideoUploadPct] = useState<number | null>(null);
  // Queue position when uploading multiple videos: "Uploading 2/5 (45%)…".
  // Single-file uploads leave this null and the label drops the "n/m" part.
  const [videoUploadQueue, setVideoUploadQueue] = useState<{
    index: number;
    total: number;
  } | null>(null);
  const refreshVideos = useCallback(async () => {
    try {
      const list = await getTourVideos(tour.full_id);
      setVideos(list);
    } catch (err) {
      console.warn('[TourView] refreshVideos failed', err);
    }
  }, [tour.full_id]);
  const handleUploadVideos = async (files: File[]) => {
    if (files.length === 0) return;
    setUploadingVideo(true);
    setVideoUploadPct(null);
    setVideoUploadQueue(
      files.length > 1 ? { index: 1, total: files.length } : null,
    );
    let succeeded = 0;
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (files.length > 1) {
          setVideoUploadQueue({ index: i + 1, total: files.length });
        }
        setVideoUploadPct(null);
        try {
          await uploadTourVideo(tour.full_id, file, {
            onProgress: (p) => setVideoUploadPct(Math.round(p.fraction * 100)),
          });
          succeeded++;
        } catch (err) {
          // Per-file error — surface and keep going so a single bad file
          // doesn't abort the rest of the queue.
          console.warn('[TourView] uploadTourVideo failed', file.name, err);
          const detail = err instanceof Error ? err.message : 'Upload failed';
          message.error(`${file.name}: ${detail}`);
        }
      }
      await refreshVideos();
      if (succeeded > 0) {
        if (files.length === 1) {
          message.success('Video uploaded');
        } else if (succeeded === files.length) {
          message.success(`Uploaded ${succeeded} videos`);
        } else {
          message.success(`Uploaded ${succeeded} of ${files.length} videos`);
        }
      }
    } finally {
      setUploadingVideo(false);
      setVideoUploadPct(null);
      setVideoUploadQueue(null);
    }
  };
  // Fetch + trigger a download for a single video. We go through fetch +
  // blob (rather than a bare <a download>) because GCS URLs are cross-
  // origin and browsers ignore the `download` attribute in that case
  // — the link would navigate-to-play instead of saving.
  const handleDownloadVideo = async (video: TourVideo) => {
    try {
      const res = await fetch(video.url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = video.filename || `${video.id}.mp4`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
    } catch (err) {
      console.warn('[TourView] downloadVideo failed', err);
      message.error('Could not download video');
    }
  };

  const [downloadingAllVideos, setDownloadingAllVideos] = useState(false);
  const handleDownloadAllVideos = async () => {
    if (videos.length === 0) return;
    setDownloadingAllVideos(true);
    try {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      await Promise.all(
        videos.map(async (v, i) => {
          try {
            const res = await fetch(v.url);
            if (!res.ok) return;
            const blob = await res.blob();
            const name = v.filename || `video-${i + 1}.mp4`;
            zip.file(name, blob);
          } catch {
            /* skip failed fetches — partial zip is better than no zip */
          }
        }),
      );
      const content = await zip.generateAsync({ type: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(content);
      a.download = 'videos.zip';
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      console.warn('[TourView] downloadAllVideos failed', err);
      message.error('Could not bundle videos');
    } finally {
      setDownloadingAllVideos(false);
    }
  };

  const handleDeleteVideo = async (videoId: string) => {
    try {
      await deleteTourVideo(tour.full_id, videoId);
      setVideos((prev) => prev.filter((v) => v.id !== videoId));
      message.success('Video deleted');
    } catch (err) {
      console.warn('[TourView] deleteTourVideo failed', err);
      const detail = err instanceof Error ? err.message : 'Delete failed';
      message.error(detail);
    }
  };

  // Floorplan-variant handlers — Developer-Mode-only writes through the
  // tour-floorplans-api GCF. After each mutation we refresh local state
  // from the GCF's response so the UI stays in lockstep with the server.
  const handleUploadFloorplan = async (file: File) => {
    setUploadingFloorplan(true);
    setFloorplanUploadPct(null);
    try {
      const result = await uploadTourFloorplan(tour.full_id, file, {
        onProgress: (p) => setFloorplanUploadPct(Math.round(p.fraction * 100)),
      });
      setFloorplanVariants((prev) => {
        const filtered = prev.filter((v) => v.id !== result.variant.id);
        return [...filtered, result.variant];
      });
      setDefaultFloorplanId(result.defaultId);
      message.success('Floorplan uploaded');
    } catch (err) {
      console.warn('[TourView] uploadTourFloorplan failed', err);
      const detail = err instanceof Error ? err.message : 'Upload failed';
      message.error(detail);
    } finally {
      setUploadingFloorplan(false);
      setFloorplanUploadPct(null);
    }
  };
  const handleSetDefaultFloorplan = async (variantId: string) => {
    const previous = defaultFloorplanId;
    setDefaultFloorplanId(variantId); // optimistic
    try {
      await setDefaultFloorplan(tour.full_id, variantId);
      message.success('Default floorplan updated');
    } catch (err) {
      console.warn('[TourView] setDefaultFloorplan failed', err);
      setDefaultFloorplanId(previous);
      message.error('Could not update default');
    }
  };
  const handleRenameFloorplan = async (variantId: string, label: string) => {
    const trimmed = label.trim();
    if (!trimmed) {
      setRenamingVariant(null);
      return;
    }
    const previous = floorplanVariants;
    // Optimistic local update so the rename feels instant.
    setFloorplanVariants((prev) =>
      prev.map((v) => (v.id === variantId ? { ...v, label: trimmed } : v)),
    );
    setRenamingVariant(null);
    try {
      await renameTourFloorplan(tour.full_id, variantId, trimmed);
    } catch (err) {
      console.warn('[TourView] renameTourFloorplan failed', err);
      setFloorplanVariants(previous);
      message.error('Could not rename floorplan');
    }
  };
  const handleDeleteFloorplan = async (variantId: string) => {
    const previous = floorplanVariants;
    const previousDefault = defaultFloorplanId;
    // Optimistic local update — promote the next surviving variant just
    // like the GCF will, so the preview stays populated without a refetch.
    const next = floorplanVariants.filter((v) => v.id !== variantId);
    setFloorplanVariants(next);
    if (defaultFloorplanId === variantId) {
      setDefaultFloorplanId(next[0]?.id ?? null);
    }
    try {
      await deleteTourFloorplan(tour.full_id, variantId);
      message.success('Floorplan deleted');
    } catch (err) {
      console.warn('[TourView] deleteTourFloorplan failed', err);
      setFloorplanVariants(previous);
      setDefaultFloorplanId(previousDefault);
      message.error('Could not delete floorplan');
    }
  };
  // Sticky "hide layer selector" preference — persisted in layer-settings.json
  // and applied to both the dashboard iframe and outgoing share URLs.
  const [shareHideLayers, setShareHideLayers] = useState(false);
  // Persistent default layer (e.g. "unfurnished"). null/"original" means
  // no &layer= param — viewer opens with the original tiles.
  const [defaultLayer, setDefaultLayer] = useState<string | null>(null);
  // Cached custom-preset list so we can resolve a human label
  // ("Scandinavian") for a default-layer key like "custom-scandinavian"
  // when rendering the share info chips.
  const [layerCustomPresets, setLayerCustomPresets] = useState<CustomPreset[]>(
    [],
  );
  // Staging-slot metadata fetched alongside layer-settings so any staging
  // layer renders with its displayName instead of the raw `slot_<ts>` key.
  const [slotsByName, setSlotsByName] = useState<SlotsByName>({});
  // Bumped whenever the Manage Layers modal closes with didChange=true,
  // so React remounts the iframe and the viewer re-reads layer-settings.
  const [iframeReloadKey, setIframeReloadKey] = useState(0);
  const closeManageLayers = (result: {
    didChange: boolean;
    hideLayers: boolean;
    defaultLayer: string | null;
    customPresets: CustomPreset[];
  }) => {
    setManageLayersOpen(false);
    setShareHideLayers(result.hideLayers);
    setDefaultLayer(result.defaultLayer);
    setLayerCustomPresets(result.customPresets);
    if (result.didChange) {
      setIframeReloadKey((k) => k + 1);
      setIframeLoaded(false);
    }
  };
  const [requestModal, setRequestModal] = useState<{
    open: boolean;
    type: string;
  }>({ open: false, type: '' });
  const openRequestModal = (type: string) =>
    setRequestModal({ open: true, type });
  const closeRequestModal = () =>
    setRequestModal((prev) => ({ ...prev, open: false }));
  const [overviewDescription, setOverviewDescription] = useState('');
  const [generatingOverview, setGeneratingOverview] = useState(false);
  const [savingOverview, setSavingOverview] = useState(false);
  const [editingOverview, setEditingOverview] = useState(false);
  const [editingInfo, setEditingInfo] = useState(false);
  const [savingInfo, setSavingInfo] = useState(false);
  // Folder membership for this tour — drives the "Move to folder" options in
  // the edit menu next to the tour name.
  const { folders, moveTour, createFolder, getFolderForTour } = useFolders();
  const currentFolderId = getFolderForTour(tour.full_id)?.id ?? null;
  const [infoName, setInfoName] = useState(tour.name || '');
  const [infoAddress, setInfoAddress] = useState(
    tour.address && tour.address !== 'not set' ? tour.address : '',
  );
  // Which variant tabs viewers see for this tour — none by default; picked
  // per tour in Developer Mode and persisted to Storage.
  const [enabledVariantIds, setEnabledVariantIds] = useState<
    Set<SpaceVariantId>
  >(new Set());
  useEffect(() => {
    let cancelled = false;
    setEnabledVariantIds(new Set());
    getTourVisibleSpaceVariants(tour.full_id)
      .then((ids) => {
        if (cancelled) return;
        const known = new Set(SPACE_VARIANTS.map((v) => v.id as string));
        setEnabledVariantIds(
          new Set(ids.filter((id) => known.has(id)) as SpaceVariantId[]),
        );
      })
      .catch((err) => {
        console.warn('[TourView] getTourVisibleSpaceVariants failed', err);
      });
    return () => {
      cancelled = true;
    };
  }, [tour.full_id]);
  const toggleVariantEnabled = useCallback(
    (id: SpaceVariantId) => {
      const prev = enabledVariantIds;
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      setEnabledVariantIds(next);
      const ordered = SPACE_VARIANTS.filter((v) => next.has(v.id)).map(
        (v) => v.id,
      );
      setTourVisibleSpaceVariants(tour.full_id, ordered).catch((err) => {
        console.error('[TourView] setTourVisibleSpaceVariants failed', err);
        setEnabledVariantIds(prev);
      });
    },
    [enabledVariantIds, tour.full_id],
  );
  const [selectedFloorplanIdx, setSelectedFloorplanIdx] = useState(0);
  const [downloadingFloorplan, setDownloadingFloorplan] = useState(false);
  // Probe state for the currently displayed floorplan. null = no URL or
  // still probing; true/false = HEAD result. Drives the Download button's
  // disabled state so a tour with a stale tourDetails entry pointing at a
  // deleted PNG doesn't expose a download path that just 404s.
  const [activeFloorplanExists, setActiveFloorplanExists] = useState<
    boolean | null
  >(null);
  const shareModal = useModal();

  // On phones the 3D tour is opened in a new tab rather than embedded —
  // an iframed WebGL viewer fights the page for touch gestures and is
  // cramped in portrait. `width === 0` means useSize hasn't measured yet.
  const { width: viewportWidth } = useSize();
  const isMobileViewport = viewportWidth > 0 && viewportWidth < 768;

  // Measured height of the desktop sticky section header — the variant tab
  // bar sticks directly against its bottom edge, so its `top` offset must
  // match the header's real rendered height (fonts/zoom can shift it).
  const sectionHeaderRef = useRef<HTMLDivElement | null>(null);
  const [sectionHeaderHeight, setSectionHeaderHeight] = useState(60);
  useEffect(() => {
    // getBoundingClientRect for subpixel accuracy; floor so the tab bar tucks
    // under the header rather than leaving a hairline gap from rounding up.
    const h = sectionHeaderRef.current?.getBoundingClientRect().height;
    if (h) setSectionHeaderHeight(Math.floor(h) - 1);
  }, [viewportWidth]);

  // Close the compact section dropdown on outside click/tap or Escape.
  useEffect(() => {
    if (!mobileMenuOpen) return undefined;
    const handlePointer = (e: MouseEvent | TouchEvent) => {
      const node = mobileMenuRef.current;
      if (node && !node.contains(e.target as Node)) {
        setMobileMenuOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileMenuOpen(false);
    };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('touchstart', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('touchstart', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [activeSection]);

  // Scroll-spy: with every section stacked in one scroll, bold the nav item
  // for whichever section currently sits near the top of the scroll area.
  useEffect(() => {
    const root = scrollContainerRef.current;
    if (!root) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const topMost = entries
          .filter((e) => e.isIntersecting)
          .sort(
            (a, b) => a.boundingClientRect.top - b.boundingClientRect.top,
          )[0];
        const id = topMost?.target.getAttribute('data-section');
        if (id) setActiveSection(id as SectionId);
      },
      { root, rootMargin: '-20% 0px -70% 0px', threshold: 0 },
    );
    Object.values(sectionRefs.current).forEach((el) => {
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  // Mirrors startLocation, but tracks only intentional "starting" choices
  // (backend hydrate + picker selection) — never the postMessage-driven
  // navigation updates. The Share link is built off this so it reflects
  // where the iframe is configured to *open*, not wherever the user has
  // walked to inside the viewer since.
  const [pinnedStartLocation, setPinnedStartLocation] =
    useState<StartLocation | null>(null);

  // Hydrate the saved start location from the backend on tour open.
  // Single GCF call per tour view; no v1 monolith dependency. Failure
  // (e.g. transient network) just means the iframe opens in default
  // mode — non-fatal. We also reset state eagerly on tour change so the
  // previous tour's start position doesn't linger.
  useEffect(() => {
    let cancelled = false;
    setStartLocation(null);
    setPinnedStartLocation(null);
    getTourStartLocation(tour.full_id)
      .then((saved) => {
        if (!cancelled && saved) {
          setStartLocation(saved as StartLocation);
          setPinnedStartLocation(saved as StartLocation);
        }
      })
      .catch((err) => {
        console.warn('[TourView] getTourStartLocation failed', err);
      });
    // Hydrate sticky layer prefs so the first iframe load + Share open
    // already reflect saved values. hideLayers + customPresets live in
    // layer-settings.json (shared with the viewer); the default layer is
    // stored separately on the tour Firestore doc — dashboard-only — and
    // fetched via collaborators-api. Subsequent updates flow back via the
    // Manage Layers onClose callback.
    setShareHideLayers(false);
    setDefaultLayer(null);
    setLayerCustomPresets([]);
    getTourLayerSettings(tour.full_id)
      .then((settings) => {
        if (!cancelled && settings) {
          setShareHideLayers(!!settings.hideLayers);
          setLayerCustomPresets(settings.customPresets ?? []);
        }
      })
      .catch((err) => {
        console.warn('[TourView] getTourLayerSettings failed', err);
      });
    getTourDashboardDefaultLayer(tour.full_id)
      .then((saved) => {
        if (!cancelled) setDefaultLayer(saved ?? null);
      })
      .catch((err) => {
        console.warn('[TourView] getTourDashboardDefaultLayer failed', err);
      });
    // One-time fetch of the staging-slot metadata for label resolution.
    // The dashboard doesn't manage slots itself, so we don't refresh on
    // modal close — a viewer-side rename surfaces only on next tour open.
    setSlotsByName({});
    fetchSlotsByName(tour.full_id).then((map) => {
      if (!cancelled) setSlotsByName(map);
    });
    // Tour analytics — atomic counters on the tour Firestore doc.
    setAnalytics(null);
    getTourAnalytics(tour.full_id)
      .then((data) => {
        if (!cancelled) setAnalytics(data);
      })
      .catch((err) => {
        console.warn('[TourView] getTourAnalytics failed', err);
      });
    // Tour owner — the original capturer shown in Settings. Owner resolution
    // may fail if the auth record can't be read; we fall back to '—'.
    setTourOwner(null);
    getCollaborators(tour.full_id)
      .then((res) => {
        if (!cancelled) setTourOwner(res.owner);
      })
      .catch((err) => {
        console.warn('[TourView] getCollaborators failed', err);
      });
    // Tour videos — public read from videos.json; the GCF only matters for
    // the Developer-Mode write paths.
    setVideos([]);
    setVideosLoading(true);
    getTourVideos(tour.full_id)
      .then((list) => {
        if (!cancelled) setVideos(list);
      })
      .catch((err) => {
        console.warn('[TourView] getTourVideos failed', err);
      })
      .finally(() => {
        if (!cancelled) setVideosLoading(false);
      });
    // Dashboard-uploaded floorplan variants. Empty list is the common
    // case for tours that haven't opted into the Developer-Mode flow yet
    // — the Floorplan Manager falls back to the v1 layouts below.
    setFloorplanVariants([]);
    setDefaultFloorplanId(null);
    setFloorplansLoading(true);
    getTourFloorplans(tour.full_id)
      .then((data) => {
        if (cancelled) return;
        setFloorplanVariants(data.variants);
        setDefaultFloorplanId(data.defaultId);
      })
      .catch((err) => {
        console.warn('[TourView] getTourFloorplans failed', err);
      })
      .finally(() => {
        if (!cancelled) setFloorplansLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tour.full_id]);

  const panoList = useMemo(() => extractPanoList(tourDetails), [tourDetails]);
  const floorplans = useMemo(
    () => extractFloorplans(tourDetails),
    [tourDetails],
  );

  // Photography variant availability. We fetch this tour's gallery.json (the
  // same public URL MediaGallery uses) so the Photography tab can hide space
  // variants that have no photos, and show a "coming soon" placeholder when
  // none of them do. Refetched on every upload/delete via galleryRefreshKey.
  const [photoImages, setPhotoImages] = useState<GalleryImage[]>([]);
  const [photosLoaded, setPhotosLoaded] = useState(false);
  useEffect(() => {
    setPhotosLoaded(false);
  }, [tour.full_id]);
  useEffect(() => {
    const tourId = getLastId(tour.full_id);
    if (!tourId) return;
    let cancelled = false;
    const url = `https://storage.googleapis.com/${STORAGE_BUCKET}/tours/${tourId}/gallery.json?_=${Date.now()}`;
    fetch(url)
      .then((res) => (res.ok ? res.json() : { images: [] }))
      .then((data: { images?: GalleryImage[] }) => {
        if (!cancelled) setPhotoImages(data.images ?? []);
      })
      .catch(() => {
        if (!cancelled) setPhotoImages([]);
      })
      .finally(() => {
        if (!cancelled) setPhotosLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [tour.full_id, galleryRefreshKey]);

  // The URL the Floorplan Manager preview is currently showing — derived
  // from the same `floorplans` array + selectedFloorplanIdx the render
  // block uses. Pulled out so the HEAD probe lives at the component body.
  // When dashboard-uploaded variants exist they supersede the v1-derived
  // floorplans entirely — those legacy entries can't be edited from here
  // and the user has explicitly opted into a fresh set via Developer Mode.
  const activeFloorplanUrl = useMemo<string | null>(() => {
    if (floorplanVariants.length > 0) {
      const idx = Math.min(
        selectedFloorplanIdx,
        Math.max(floorplanVariants.length - 1, 0),
      );
      return floorplanVariants[idx]?.url ?? null;
    }
    const withUrls = floorplans.filter((fp) => fp.thumbnailUrl);
    if (withUrls.length === 0) return null;
    const idx = Math.min(
      selectedFloorplanIdx,
      Math.max(withUrls.length - 1, 0),
    );
    return withUrls[idx]?.thumbnailUrl ?? null;
  }, [floorplans, floorplanVariants, selectedFloorplanIdx]);
  // Snap the layout selector to the default variant whenever the
  // variant list or defaultId changes (tour open, upload, set-default).
  useEffect(() => {
    if (floorplanVariants.length === 0 || !defaultFloorplanId) return;
    const idx = floorplanVariants.findIndex((v) => v.id === defaultFloorplanId);
    if (idx >= 0) setSelectedFloorplanIdx(idx);
  }, [floorplanVariants, defaultFloorplanId]);

  useEffect(() => {
    if (!activeFloorplanUrl) {
      setActiveFloorplanExists(null);
      return;
    }
    let cancelled = false;
    setActiveFloorplanExists(null);
    fetch(activeFloorplanUrl, { method: 'HEAD' })
      .then((res) => {
        if (!cancelled) setActiveFloorplanExists(res.ok);
      })
      .catch(() => {
        if (!cancelled) setActiveFloorplanExists(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeFloorplanUrl]);

  const startOptions: StartOption[] = useMemo(
    () => [...floorplans, ...panoList],
    [floorplans, panoList],
  );
  const defaultFloorplan = floorplans[0] ?? null;

  // The dashboard iframe + share URLs both derive from the sticky
  // defaultLayer (Manage Layers) — not the per-session spaceVariant which
  // now only filters the Photography gallery.
  const effectiveLayer =
    defaultLayer && defaultLayer !== 'original' ? defaultLayer : undefined;
  // When true, the embedded 3D tour swaps from the read-only client viewer
  // to the Stager editor link (REACT_APP_STAGER_URL via the 'edit' mode).
  const [editTools, setEditTools] = useState(false);
  // Flythrough path-drawing reuses the Stager editor (same as Edit Tools) but
  // additionally tells the viewer to open in flythrough-edit mode.
  const [flythroughEdit, setFlythroughEdit] = useState(false);
  // Set-Start-Location edit mode. Unlike flythroughEdit this points at
  // the read-only viewer URL (not the Stager) because the mode doesn't
  // change viewer input — it just surfaces a Save/Cancel toolbar for
  // the current URL.
  const [startLocationEdit, setStartLocationEdit] = useState(false);
  const iframeSrc = useMemo(
    () =>
      buildTourLink(
        tour,
        startLocation,
        editTools || flythroughEdit ? 'edit' : 'view', // 'edit' points the iframe at the Stager link
        effectiveLayer,
        shareHideLayers,
        true, // noTrack — dashboard preview shouldn't count toward analytics
        flythroughEdit, // appends &flythroughEdit=1
        startLocationEdit, // appends &startLocationEdit=1
      ),
    [
      tour.full_id,
      tour.name,
      startLocation,
      effectiveLayer,
      shareHideLayers,
      editTools,
      flythroughEdit,
      startLocationEdit,
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
  );
  // Share URL is anchored to the pinned start (backend hydrate / picker),
  // not the live startLocation that drifts as the viewer fires nav events.
  const shareUrl = useMemo(
    () =>
      buildTourLink(
        tour,
        pinnedStartLocation,
        'view',
        effectiveLayer,
        shareHideLayers,
      ),
    [
      tour.full_id,
      tour.name,
      pinnedStartLocation,
      effectiveLayer,
      shareHideLayers,
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
  );
  // Skeleton overlay shown until the viewer iframe fires onLoad. Reset on
  // every URL change (start location / layer edits) to re-pulse the placeholder.
  const [iframeLoaded, setIframeLoaded] = useState(false);
  useEffect(() => {
    setIframeLoaded(false);
  }, [iframeSrc]);

  // Push the hideLayers preference to the iframe via postMessage as soon
  // as it loads, and again whenever the preference flips at runtime. The
  // viewer also respects `&hideLayers=1` in the src, but URL-based config
  // only applies at mount; this re-asserts after any internal nav.
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  useEffect(() => {
    if (!iframeLoaded) return;
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    win.postMessage(
      { type: 'viewer-set-ui', hideLayers: shareHideLayers },
      IFRAME_ORIGIN ?? '*',
    );
  }, [iframeLoaded, shareHideLayers, iframeReloadKey]);

  const handleSelectStart = (option: StartOption) => {
    const next: StartLocation =
      option.kind === 'floorplan'
        ? {
            type: 'floorplan',
            floorName: option.floorName,
            thumbnailUrl: option.thumbnailUrl ?? undefined,
            fit: 'contain',
          }
        : {
            type: 'pano',
            panoId: option.id,
            folderPath: option.fullPath,
            roomName: option.roomName,
            floorName: option.floorName,
            thumbnailUrl: option.thumbnailUrl,
            fit: 'cover',
          };
    // Optimistic local update + persist to the tour doc so the choice
    // sticks across devices/sessions. Non-fatal on failure — user keeps
    // the in-session selection even if the network call fails.
    setStartLocation(next);
    setPinnedStartLocation(next);
    setTourStartLocation(tour.full_id, next).catch((err) => {
      console.warn('[TourView] setTourStartLocation failed', err);
    });
    setChangeStartOpen(false);
  };

  // Pick what to show in the card preview. Priority:
  // 1. User-selected location's thumbnail
  // 2. First floor's floorplan SVG (if present in JSON)
  // 3. null → render a generic floorplan icon
  const cardPreviewIsFloorplan =
    startLocation?.type === 'floorplan' ||
    (!startLocation && !!defaultFloorplan);
  const cardPreviewUrl =
    startLocation?.thumbnailUrl ?? defaultFloorplan?.thumbnailUrl ?? null;
  const cardPreviewFit: 'cover' | 'contain' =
    startLocation?.fit ?? (cardPreviewIsFloorplan ? 'contain' : 'cover');

  // Prepopulation lives inside the tourDetails fetch effect below — this
  // intentionally avoids racing the user's typing during in-flight loads.

  const handleOverviewGenerate = async () => {
    if (generatingOverview) return;
    const trimmedAddress = (tour.address || '').trim();
    if (!trimmedAddress || trimmedAddress === 'not set') {
      message.error('Add a tour address first so the AI has context.');
      return;
    }
    if (!panoList.length) {
      message.error('No panoramas found for this tour yet.');
      return;
    }
    setGeneratingOverview(true);
    try {
      const { description } = await generateTourDescription({
        address: trimmedAddress,
        name: tour.name?.trim() || undefined,
        panoUrls: panoList.map((p) => p.thumbnailUrl),
      });
      setOverviewDescription(description);
      message.success('Description generated — review and save when ready.');
    } catch (err) {
      console.error('[Overview] AI generate failed', err);
      message.error(
        (err as Error).message || 'Failed to generate description.',
      );
    } finally {
      setGeneratingOverview(false);
    }
  };

  const handleOverviewSave = async () => {
    setSavingOverview(true);
    try {
      const tourId = getLastId(tour.full_id) ?? '';
      const existingContact = (tourDetails?.contactInfo ??
        {}) as TourContactInfo;
      const contact: TourContactInfo = {
        name: existingContact.name ?? '',
        title: existingContact.title ?? '',
        email: existingContact.email ?? '',
        phone: existingContact.phone ?? '',
        photo: existingContact.photo ?? '',
        logo: existingContact.logo ?? '',
        website: existingContact.website ?? '',
      };
      // Route description writes through the gen2 collaborators-api action
      // (same path as the Tour Info editor) so collaborators — not just the
      // tour creator — can save. The legacy v1 `tours-updateTour` callable
      // rejected non-creator callers, surfacing as "Failed to save". This
      // writes to the Firestore tour doc, which the Overview now treats as
      // canonical on next mount (see the tourDetails.json fetch effect).
      await updateTourInfo({
        tourId: `tours/${tourId}`,
        name: infoName.trim() || 'Untitled Tour',
        address: infoAddress.trim() || 'not set',
        description: overviewDescription,
        contactInfo: contact,
      });
      setTourDetails((prev) =>
        prev ? { ...prev, description: overviewDescription } : prev,
      );
      message.success('Description saved');
      setEditingOverview(false);
    } catch (err) {
      console.error('[Overview] save failed', err);
      message.error('Failed to save description');
    } finally {
      setSavingOverview(false);
    }
  };

  const handleInfoSave = async () => {
    setSavingInfo(true);
    try {
      const tourId = getLastId(tour.full_id) ?? '';
      const existingContact = (tourDetails?.contactInfo ??
        {}) as TourContactInfo;
      const contact: TourContactInfo = {
        name: existingContact.name ?? '',
        title: existingContact.title ?? '',
        email: existingContact.email ?? '',
        phone: existingContact.phone ?? '',
        photo: existingContact.photo ?? '',
        logo: existingContact.logo ?? '',
        website: existingContact.website ?? '',
      };
      await updateTourInfo({
        tourId: `tours/${tourId}`,
        name: infoName.trim() || 'Untitled Tour',
        address: infoAddress.trim() || 'not set',
        description: overviewDescription,
        contactInfo: contact,
      });
      setInfoName(infoName.trim() || 'Untitled Tour');
      setInfoAddress(infoAddress.trim());
      message.success('Tour details saved');
      setEditingInfo(false);
    } catch (err) {
      console.error('[TourInfo] save failed', err);
      message.error('Failed to save tour details');
    } finally {
      setSavingInfo(false);
    }
  };

  useEffect(() => {
    const tourId = getLastId(tour.full_id);
    if (!tourId) return;
    const bucket =
      process.env.REACT_APP_STORAGE_BUCKET || 'id3d-prod.appspot.com';
    // Cache-bust each fetch: GCS doesn't always set no-cache on v1-written
    // tourDetails.json, so browser-cached copies (sometimes missing newly-
    // saved fields like description) would persist across reloads otherwise.
    const detailsUrl = `https://storage.googleapis.com/${bucket}/tours/${tourId}/tourDetails.json?_=${Date.now()}`;

    // Reset stale data from the previous tour eagerly so we never flash the
    // old description into the textarea while waiting on the new fetch.
    let cancelled = false;
    setTourDetails(null);
    setOverviewDescription('');
    fetch(detailsUrl)
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data: TourDetailsJson) => {
        if (cancelled) return;
        setTourDetails(data);
        // The Firestore tour doc is canonical for the description: saves go
        // through the collaborators-api action, which writes there. Prefer
        // it whenever the field exists, and fall back to the JSON copy only
        // for legacy tours whose description was never written to Firestore.
        const firestoreDescription = (tour as { description?: unknown })
          .description;
        const hasFirestoreDescription =
          typeof firestoreDescription === 'string';
        setOverviewDescription(
          hasFirestoreDescription
            ? (firestoreDescription as string)
            : data.description ?? '',
        );
        console.log('[TourView] tourDetails.json', { url: detailsUrl, data });
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[TourView] failed to fetch tourDetails.json', {
          url: detailsUrl,
          error: err,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [tour.full_id]);

  // Probe for the canonical custom thumbnail at tours/{id}/thumbnail.jpg
  // (written by tour-thumbnail-api). Used in place of the legacy
  // tour.thumbnailUrl when it exists. We keep heroThumbnail null while
  // probing so the lower-priority pano-thumb fallback doesn't render for
  // a split second and then get replaced — that was the visible flash.
  const customThumbUrl = useMemo(() => {
    const id = getLastId(tour.full_id);
    if (!id) return null;
    const bucket =
      process.env.REACT_APP_STORAGE_BUCKET || 'id3d-prod.appspot.com';
    return `https://storage.googleapis.com/${bucket}/tours/${id}/thumbnail.jpg`;
  }, [tour.full_id]);
  const [customThumbStatus, setCustomThumbStatus] = useState<
    'probing' | 'exists' | 'missing'
  >('probing');
  useEffect(() => {
    if (!customThumbUrl) {
      setCustomThumbStatus('missing');
      return;
    }
    let cancelled = false;
    setCustomThumbStatus('probing');
    fetch(`${customThumbUrl}?_=${Date.now()}`, { method: 'HEAD' })
      .then((res) => {
        if (cancelled) return;
        setCustomThumbStatus(res.ok ? 'exists' : 'missing');
      })
      .catch(() => {
        if (!cancelled) setCustomThumbStatus('missing');
      });
    return () => {
      cancelled = true;
    };
  }, [customThumbUrl]);

  // Editable total floor area on the Floorplan Manager card. Persisted
  // per tour via the tourArea GCF. 404 just means it's never been set.
  const [areaValue, setAreaValue] = useState<number | null>(null);
  const [editingArea, setEditingArea] = useState(false);
  const [areaInput, setAreaInput] = useState('');
  const [savingArea, setSavingArea] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setAreaValue(null);
    setEditingArea(false);
    setAreaInput('');
    getTourArea(tour.full_id)
      .then((data) => {
        if (cancelled) return;
        if (data && typeof data.value === 'number') setAreaValue(data.value);
      })
      .catch((err) => {
        console.warn('[TourView] getTourArea failed', err);
      });
    return () => {
      cancelled = true;
    };
  }, [tour.full_id]);

  const startAreaEdit = () => {
    setAreaInput(areaValue != null ? String(areaValue) : '');
    setEditingArea(true);
  };
  const commitAreaEdit = async () => {
    const trimmed = areaInput.trim();
    const next = trimmed === '' ? null : Number(trimmed.replace(/,/g, ''));
    if (next !== null && (!Number.isFinite(next) || next < 0)) {
      setEditingArea(false);
      setAreaInput('');
      return;
    }
    setEditingArea(false);
    setSavingArea(true);
    const prev = areaValue;
    setAreaValue(next);
    try {
      await setTourArea(tour.full_id, next);
    } catch (err) {
      console.warn('[TourView] setTourArea failed', err);
      setAreaValue(prev);
    } finally {
      setSavingArea(false);
    }
  };
  const cancelAreaEdit = () => {
    setEditingArea(false);
    setAreaInput('');
  };

  useEffect(() => {
    // The reset on tour change is handled by the hydration effect above —
    // here we just bind/unbind the iframe postMessage listener.
    const handleMessage = (event: MessageEvent) => {
      if (IFRAME_ORIGIN && event.origin !== IFRAME_ORIGIN) return;
      const payload = event.data;
      if (!payload || typeof payload !== 'object') return;

      if (
        payload.type === 'start-location' ||
        payload.type === 'panoid-change' ||
        payload.type === 'navigation'
      ) {
        console.log('[TourView] iframe message', payload);
        setStartLocation({
          type: 'pano',
          panoId: payload.panoId ?? payload.panoid ?? payload.id,
          folderPath: payload.folderPath ?? payload.path ?? payload.folder,
          url: payload.url ?? payload.href,
          fit: 'cover',
        });
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [tour.full_id]);

  // While the custom-thumbnail probe is still in flight, render the
  // gradient placeholder (heroThumbnail=null) instead of a lower-priority
  // value that might briefly flash before being replaced.
  const heroThumbnail =
    customThumbStatus === 'probing'
      ? null
      : customThumbStatus === 'exists'
      ? customThumbUrl
      : tour.thumbnailUrl ??
        cardPreviewUrl ??
        panoList[0]?.thumbnailUrl ??
        null;

  const scrollToSection = (id: SectionId) => {
    sectionRefs.current[id]?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
    setActiveSection(id);
  };

  const renderSidebarNav = (onNavigate?: () => void) =>
    SECTIONS.map((s) => {
      const active = activeSection === s.id;
      return (
        <button
          key={s.id}
          type="button"
          onClick={() => {
            scrollToSection(s.id);
            onNavigate?.();
          }}
          className={classNames(
            'flex items-center gap-3 px-3 h-10 rounded-xl text-[13.5px] transition-colors bg-transparent border-0 outline-none cursor-pointer w-full text-left',
            active
              ? 'bg-black/[0.07] text-black/[0.85] font-semibold'
              : 'text-black/55 hover:text-black/80 hover:bg-black/[0.04] font-medium',
          )}
          aria-current={active ? 'page' : undefined}
        >
          <span
            className={classNames(
              'flex items-center justify-center shrink-0',
              active ? 'text-black/85' : 'text-black/50',
            )}
          >
            {s.icon}
          </span>
          <span className="truncate">{s.label}</span>
        </button>
      );
    });

  const renderSectionBody = () => {
    const renderOverview = () => {
      return (
        <>
          <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
            <div className="min-w-0 flex-1">
              {editingInfo ? (
                <div className="flex flex-col gap-2 max-w-md">
                  <input
                    value={infoName}
                    onChange={(e) => setInfoName(e.target.value)}
                    placeholder="Tour name"
                    className="w-full px-3 py-2 border border-black/15 rounded-xl text-[20px] font-bold text-[#151A30] outline-none focus:border-black/40 transition-colors"
                  />
                  <input
                    value={infoAddress}
                    onChange={(e) => setInfoAddress(e.target.value)}
                    placeholder="Address"
                    className="w-full px-3 py-2 border border-black/15 rounded-xl text-[13.5px] text-black/70 outline-none focus:border-black/40 transition-colors"
                  />
                  <div className="flex items-center gap-2 mt-1">
                    <button
                      type="button"
                      onClick={() => {
                        setInfoName(tour.name || '');
                        setInfoAddress(
                          tour.address && tour.address !== 'not set'
                            ? tour.address
                            : '',
                        );
                        setEditingInfo(false);
                      }}
                      disabled={savingInfo}
                      className="inline-flex items-center h-9 px-4 rounded-full text-[13px] font-medium text-black/70 bg-transparent hover:bg-black/[0.04] border border-black/10 transition-colors cursor-pointer focus:outline-none disabled:opacity-60"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleInfoSave}
                      disabled={savingInfo}
                      className="inline-flex items-center h-9 px-4 rounded-full text-[13px] font-semibold text-white bg-black/85 hover:bg-black transition-colors border-0 cursor-pointer focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {savingInfo ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 min-w-0">
                    <h1 className="text-[28px] font-bold tracking-tight text-[#151A30] m-0 truncate max-md:text-[22px]">
                      {infoName || 'Untitled Tour'}
                    </h1>
                    <MoveToFolderMenu
                      folders={folders}
                      currentFolderId={currentFolderId}
                      onMove={(folderId) => moveTour(tour.full_id, folderId)}
                      onCreate={(name) => createFolder(name).id}
                      triggerAriaLabel="Move tour to a folder or rename it"
                      triggerClassName="inline-flex items-center justify-center w-8 h-8 rounded-full bg-black/[0.05] hover:bg-black/10 transition-colors border-0 text-black/70 cursor-pointer focus:outline-none shrink-0"
                      trigger={
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          stroke="none"
                        >
                          <circle cx="4" cy="10" r="1.6" />
                          <circle cx="10" cy="10" r="1.6" />
                          <circle cx="16" cy="10" r="1.6" />
                        </svg>
                      }
                      primaryAction={{
                        label: 'Rename',
                        icon: (
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 16 16"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M11.5 2.5l2 2-9 9-2.5.5.5-2.5 9-9z" />
                          </svg>
                        ),
                        onClick: () => setEditingInfo(true),
                      }}
                    />
                  </div>
                  {infoAddress && (
                    <p className="text-[13.5px] text-black/55 m-0 mt-1">
                      {infoAddress}
                    </p>
                  )}
                </>
              )}
            </div>
            {/* Analytics stats — sit to the right of the title. */}
            <div className="grid grid-cols-3 gap-3 shrink-0 w-[760px] max-lg:w-[520px] max-md:w-full">
              <StatCard
                label="Total Views"
                value={
                  analytics ? (analytics.views ?? 0).toLocaleString() : '—'
                }
              />
              <StatCard
                label="Avg. Time"
                value={
                  analytics
                    ? formatAvgDuration(
                        analytics.totalSessionMs,
                        analytics.sessionCount,
                      )
                    : '—'
                }
              />
              <StatCard
                label="Shares"
                value={
                  analytics ? (analytics.shares ?? 0).toLocaleString() : '—'
                }
              />
            </div>
          </div>

          {/* Description card was moved to the 3D Tour section, under the
              Display Settings buttons (full width). */}
        </>
      );
    };

    const renderOverviewDescriptionCard = () => {
      return (
        <div className="bg-white rounded-3xl border border-black/[0.06] shadow-[0_4px_16px_rgba(0,0,0,0.06)] p-5 flex flex-col min-h-[220px]">
          <div className="flex items-center justify-between gap-3 mb-3">
            <h3 className="text-[14px] font-semibold text-black/85 m-0">
              Description
            </h3>
            {editingOverview ? (
              <button
                type="button"
                onClick={handleOverviewGenerate}
                disabled={generatingOverview || savingOverview}
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[12px] font-semibold text-white bg-black/85 hover:bg-black shadow-[0_2px_8px_rgba(0,0,0,0.12)] transition-colors border-0 cursor-pointer focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className={generatingOverview ? 'animate-spin' : ''}
                >
                  {generatingOverview ? (
                    <path d="M10 2a8 8 0 018 8h-2a6 6 0 00-6-6V2z" />
                  ) : (
                    <path d="M10 1l1.5 5.5L17 8l-5.5 1.5L10 15l-1.5-5.5L3 8l5.5-1.5L10 1zM15.5 13l.75 2.25L18.5 16l-2.25.75L15.5 19l-.75-2.25L12.5 16l2.25-.75L15.5 13z" />
                  )}
                </svg>
                {generatingOverview ? 'Generating…' : 'Generate with AI'}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setEditingOverview(true)}
                aria-label="Edit description"
                className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-black/[0.05] hover:bg-black/10 transition-colors border-0 text-black/70 cursor-pointer focus:outline-none"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M11.5 2.5l2 2-9 9-2.5.5.5-2.5 9-9z" />
                </svg>
              </button>
            )}
          </div>
          <textarea
            value={overviewDescription}
            onChange={(e) => setOverviewDescription(e.target.value)}
            readOnly={!editingOverview}
            placeholder="Tell us a little bit about your tour..."
            rows={6}
            className={classNames(
              'w-full px-3 py-2.5 border rounded-xl text-[13.5px] text-black/85 placeholder:text-black/40 outline-none transition-colors resize-none overflow-hidden [field-sizing:content]',
              editingOverview
                ? 'bg-white border-black/10 focus:border-black/40'
                : 'bg-transparent border-transparent cursor-default',
            )}
          />
          {editingOverview && (
            <div className="flex items-center justify-end gap-2 mt-3">
              <button
                type="button"
                onClick={() => {
                  setOverviewDescription(tourDetails?.description ?? '');
                  setEditingOverview(false);
                }}
                disabled={savingOverview || generatingOverview}
                className="inline-flex items-center h-9 px-4 rounded-full text-[13px] font-medium text-black/70 bg-transparent hover:bg-black/[0.04] border border-black/10 transition-colors cursor-pointer focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleOverviewSave}
                disabled={savingOverview || generatingOverview}
                className="inline-flex items-center h-9 px-4 rounded-full text-[13px] font-semibold text-white bg-black/85 hover:bg-black transition-colors border-0 cursor-pointer focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {savingOverview ? 'Saving…' : 'Save'}
              </button>
            </div>
          )}
        </div>
      );
    };
    const renderTour = () => {
      return (
        <>
          <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
            <h1 className="text-[22px] font-bold text-[#151A30] m-0">
              3D Tour
            </h1>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => {
                  // Mobile has no inline iframe — open the editor in a new tab.
                  if (isMobileViewport) {
                    window.open(
                      buildTourLink(
                        tour,
                        startLocation,
                        'edit',
                        effectiveLayer,
                        shareHideLayers,
                        true,
                      ),
                      '_blank',
                      'noopener,noreferrer',
                    );
                    return;
                  }
                  setFlythroughEdit(false);
                  setEditTools((v) => !v);
                }}
                aria-pressed={editTools}
                className={`inline-flex items-center gap-2 h-9 px-4 rounded-full text-[13px] font-semibold border transition-colors ${
                  editTools
                    ? 'bg-[#151A30] text-white border-[#151A30]'
                    : 'bg-white text-[#151A30] border-[#151A30]/15 hover:border-[#151A30]/40'
                }`}
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 20h9" />
                  <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" />
                </svg>
                {editTools ? 'Exit Edit Tools' : 'Edit Tools'}
              </button>
              <button
                type="button"
                onClick={() =>
                  window.open(
                    buildTourLink(
                      tour,
                      startLocation,
                      'edit',
                      effectiveLayer,
                      shareHideLayers,
                    ),
                    '_blank',
                    'noopener,noreferrer',
                  )
                }
                className="inline-flex items-center gap-2 h-9 px-4 rounded-full text-[13px] font-semibold border transition-colors bg-white text-[#151A30] border-[#151A30]/15 hover:border-[#151A30]/40"
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M15 3h6v6" />
                  <path d="M10 14 21 3" />
                  <path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" />
                </svg>
                Expand in new window
              </button>
            </div>
          </div>

          {/* The 3D tour always shows the actual space — the space-variant
              tabs (Empty / Reno A / Reno B / Reno C) only filter the Photography
              gallery and never touch the embedded viewer. */}
          <div className="relative mt-2 mx-auto w-full max-w-[360px] aspect-[9/16] md:max-w-[calc(72vh*16/9)] md:aspect-[16/9] rounded-[1.5rem] overflow-hidden bg-black/[0.04] mb-8">
            {viewportWidth === 0 ? (
              <div className="absolute inset-0 bg-black/10 animate-pulse" />
            ) : isMobileViewport ? (
              <button
                type="button"
                onClick={() =>
                  window.open(iframeSrc, '_blank', 'noopener,noreferrer')
                }
                aria-label="Open the 3D tour in a new tab"
                className="absolute inset-0 w-full h-full p-0 border-0 cursor-pointer overflow-hidden focus:outline-none"
              >
                {heroThumbnail ? (
                  <img
                    src={heroThumbnail}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 bg-[rgb(38,38,38)]" />
                )}
                <div className="absolute inset-0 bg-black/45 flex flex-col items-center justify-center text-center px-8">
                  <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/95 mb-4 shadow-[0_4px_16px_rgba(0,0,0,0.3)]">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="#151A30"
                    >
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </span>
                  <span className="text-white text-[15px] font-semibold">
                    Tap to open the 3D tour
                  </span>
                  <span className="text-white/75 text-[12.5px] mt-1 inline-flex items-center gap-1">
                    Opens in a new tab
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M9 2h5v5" />
                      <path d="M14 2L7 9" />
                      <path d="M12 9.5V13a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h3.5" />
                    </svg>
                  </span>
                </div>
              </button>
            ) : (
              <>
                <iframe
                  ref={iframeRef}
                  key={iframeReloadKey}
                  className="absolute inset-0 w-full h-full border-0 block"
                  src={iframeSrc}
                  title={tour.name}
                  allow="fullscreen; accelerometer; gyroscope; magnetometer; xr-spatial-tracking; screen-wake-lock"
                  allowFullScreen
                  onLoad={() => setIframeLoaded(true)}
                />
                {!iframeLoaded && (
                  <div className="absolute inset-0 bg-black/10 animate-pulse pointer-events-none" />
                )}
              </>
            )}
          </div>

          <div className="mb-5">
            <h2 className="text-[16px] font-semibold text-black/85 m-0">
              Display Settings
            </h2>
            <p className="text-[12px] text-black/55 mt-1 m-0 max-w-2xl">
              Customize how this space appears when embedded.
            </p>
          </div>

          <div className="grid grid-cols-4 gap-3 auto-rows-fr max-lg:grid-cols-3 max-md:grid-cols-2 max-sm:grid-cols-1">
            <SettingCard
              icon={
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M8 5v14l11-7z" />
                </svg>
              }
              title="Flythrough"
              description="Draw a path to guide viewers in a tour."
              onClick={() => {
                // Mobile has no inline iframe — open the editor in a new tab.
                if (isMobileViewport) {
                  window.open(
                    buildTourLink(
                      tour,
                      startLocation,
                      'edit',
                      effectiveLayer,
                      shareHideLayers,
                      true,
                      true, // flythroughEdit
                    ),
                    '_blank',
                    'noopener,noreferrer',
                  );
                  return;
                }
                setEditTools(false);
                setFlythroughEdit((v) => !v);
              }}
              ariaLabel="Create a flythrough path"
              preview={
                <div className="absolute inset-0 w-full h-full overflow-hidden flex items-center justify-center bg-black/85 text-white/90">
                  <svg
                    width="64"
                    height="64"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
              }
            />

            <SettingCard
              icon={
                // Reuses the same navigation-arrow glyph as the card's
                // preview slot (solid fill, viewBox 0 0 24 24) so the
                // header + preview read as one identity — mirroring how
                // Flythrough uses the play triangle in both slots.
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 4l7 15-7-4-7 4 7-15z" />
                </svg>
              }
              title="Start Location"
              description="Aim the camera in the viewer, then Save."
              onClick={() => {
                // Entering = the click that turns start-location-edit
                // ON (as opposed to a second click that turns it off).
                // Read from the current state so both card branches
                // (mobile new-tab / desktop toggle) share the same
                // entering-vs-exiting semantics.
                const entering = !startLocationEdit;

                // Mobile: no inline iframe, so open the viewer in a new
                // tab already in Set-Start-Location edit mode. Mode
                // 'edit' routes to REACT_APP_STAGER_URL so the new tab
                // matches what the desktop iframe would load (parity
                // with the "invoke edit tools" behavior below).
                if (isMobileViewport) {
                  if (entering) {
                    window.open(
                      buildTourLink(
                        tour,
                        startLocation,
                        'edit',
                        effectiveLayer,
                        shareHideLayers,
                        true, // noTrack
                        false, // flythroughEdit
                        true, // startLocationEdit
                      ),
                      '_blank',
                      'noopener,noreferrer',
                    );
                  }
                  return;
                }

                // Desktop: invoke Edit Tools + the start-location param
                // together, so the iframe swaps to the Stager URL and
                // the "Edit Tools" pill in the header lights up. Second
                // click turns both back off — we treat editTools as an
                // implicit "we brought you here" flag, so exiting the
                // start-location mode also exits edit tools even if the
                // user hadn't manually toggled it before.
                setFlythroughEdit(false);
                setEditTools(entering);
                setStartLocationEdit(entering);
              }}
              ariaLabel="Set start location in the viewer"
              preview={
                // Single visual identity: dark background + white
                // navigation-arrow glyph, matching Flythrough + Manage
                // Layers. Legacy picker-thumbnail path is still honored
                // when populated (older saves may carry cardPreviewUrl);
                // URL-based saves fall through to the arrow.
                cardPreviewUrl ? (
                  <div
                    className={classNames(
                      'absolute inset-0 w-full h-full overflow-hidden flex items-center justify-center',
                      cardPreviewIsFloorplan
                        ? 'bg-[rgb(77,77,77)]'
                        : 'bg-black/[0.04]',
                    )}
                  >
                    <img
                      src={cardPreviewUrl}
                      alt=""
                      className={classNames(
                        'w-full h-full',
                        cardPreviewFit === 'contain'
                          ? 'object-contain p-2 md:p-4'
                          : 'object-cover',
                      )}
                    />
                  </div>
                ) : (
                  <div className="absolute inset-0 w-full h-full overflow-hidden flex items-center justify-center bg-black/85">
                    <svg
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="w-1/2 h-1/2 text-white"
                    >
                      <path d="M12 4l7 15-7-4-7 4 7-15z" />
                    </svg>
                  </div>
                )
              }
            />

            <SettingCard
              icon={
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M10 3l7 4-7 4-7-4 7-4z" />
                  <path d="M3 11l7 4 7-4" />
                  <path d="M3 15l7 4 7-4" />
                </svg>
              }
              title="Manage Layers"
              description="Toggle which layer pills appear in the viewer."
              onClick={() => setManageLayersOpen(true)}
              ariaLabel="Manage layers"
              preview={
                <div className="absolute inset-0 w-full h-full overflow-hidden flex items-center justify-center bg-black/85">
                  {/* Remade two-layer stack icon (from remade icon.svg). */}
                  <svg
                    viewBox="0 0 134 144"
                    fill="currentColor"
                    className="w-1/2 h-1/2 text-white"
                  >
                    <path d="M66.8178 102.627L0.817764 50.6273L66.8178 0.627274L132.818 50.6273L66.8178 102.627Z" />
                    <path d="M133.635 90.6195L133.127 91.0199L67.1273 143.02L66.8178 143.264L66.5082 143.02L0.508194 91.0199L0.00038147 90.6195L10.9994 82.2865L66.5082 126.02L66.8178 126.264L67.1273 126.02L122.635 82.2865L133.635 90.6195Z" />
                  </svg>
                </div>
              }
            />

            <SettingCard
              icon={
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="2.5" y="3.5" width="15" height="13" rx="2" />
                  <circle cx="7" cy="8" r="1.5" />
                  <path d="M3 15l4.5-4.5 3.5 3.5 2.5-2.5L17.5 15" />
                </svg>
              }
              title="Set Thumbnail"
              description="Set a thumbnail for the dashboard icon."
              ariaLabel="Set dashboard thumbnail"
              preview={
                <div className="absolute inset-0 w-full h-full overflow-hidden flex items-center justify-center bg-black/85">
                  {heroThumbnail ? (
                    <img
                      src={heroThumbnail}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-black/15 via-black/10 to-black/[0.04]" />
                  )}
                </div>
              }
            />
          </div>

          {/* Tour description — full width, beneath the Display Settings
              buttons (Flythrough / Start Location / …). */}
          <div className="mt-4">{renderOverviewDescriptionCard()}</div>
        </>
      );
    };

    const renderPictures = () => {
      // Actual Space is always a column; the other variants are opt-in per
      // tour. Developer Mode shows every column so any slot can be filled.
      const photoColumns = SPACE_VARIANTS.filter(
        (v) => devMode || v.id === 'existing' || enabledVariantIds.has(v.id),
      );
      return (
        <>
          {devMode && (
            <div className="flex flex-wrap items-center gap-2 mb-4 px-4 py-3 rounded-2xl border border-dashed border-black/15 bg-black/[0.02]">
              <span className="text-[12px] font-medium text-black/55 mr-1">
                Columns shown to viewers:
              </span>
              {SPACE_VARIANTS.filter((s) => s.id !== 'existing').map((s) => {
                const on = enabledVariantIds.has(s.id);
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => toggleVariantEnabled(s.id)}
                    aria-pressed={on}
                    className={classNames(
                      'inline-flex items-center gap-1.5 h-8 px-3 text-[12.5px] rounded-full leading-none whitespace-nowrap transition-colors cursor-pointer focus:outline-none border',
                      on
                        ? 'bg-black/85 text-white border-black/85 font-semibold'
                        : 'bg-white text-black/60 border-black/15 hover:text-black/85 font-medium',
                    )}
                  >
                    {on && (
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path d="M7.5 13.4 4.1 10l-1.4 1.4 4.8 4.8L17.3 6.4 15.9 5z" />
                      </svg>
                    )}
                    {s.label}
                  </button>
                );
              })}
            </div>
          )}
          <PhotoComparisonGrid
            tour={tour}
            images={photoImages}
            loaded={photosLoaded}
            columns={photoColumns}
            devMode={devMode}
            stickyTop={isMobileViewport ? undefined : sectionHeaderHeight}
            onMutated={bumpGallery}
            onRequest={() => openRequestModal('Request New Interior Design')}
          />
        </>
      );
    };

    const renderFloorplan = () => {
      // Variants from the dashboard upload flow take precedence over the
      // v1-monolith floorplans on tourDetails — once the user has opted
      // into Developer-Mode uploads, this becomes the source of truth.
      const usingVariants = floorplanVariants.length > 0;
      const floorplansWithUrls = floorplans.filter((fp) => fp.thumbnailUrl);
      const layouts: { id: string; label: string; url: string | null }[] =
        usingVariants
          ? floorplanVariants.map((v) => ({
              id: v.id,
              label: v.label,
              url: v.url,
            }))
          : floorplansWithUrls.length > 0
          ? floorplansWithUrls.map((fp, i) => ({
              id: fp.id,
              label: fp.floorName
                ? `Floor ${fp.floorName}`
                : `Layout ${String.fromCharCode(65 + i)}`,
              url: fp.thumbnailUrl ?? null,
            }))
          : [];
      const activeIdx = Math.min(
        selectedFloorplanIdx,
        Math.max(layouts.length - 1, 0),
      );
      const activeLayout = layouts[activeIdx];

      const handleDownloadFloorplan = async () => {
        if (!activeLayout?.url) {
          message.error('No floorplan available to download.');
          return;
        }
        setDownloadingFloorplan(true);
        try {
          const res = await fetch(activeLayout.url);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const blob = await res.blob();
          // Derive the extension from the source URL (typically .png) so
          // the saved file actually opens in image viewers. The previous
          // hardcoded `.svg` was wrong — floorplans are PNGs.
          const ext = (() => {
            try {
              const path = new URL(activeLayout.url).pathname;
              const m = /\.([a-z0-9]+)$/i.exec(path);
              return m ? m[1].toLowerCase() : 'png';
            } catch {
              return 'png';
            }
          })();
          const a = document.createElement('a');
          a.href = URL.createObjectURL(blob);
          a.download = `floorplan-${activeLayout.label
            .replace(/\s+/g, '-')
            .toLowerCase()}.${ext}`;
          a.click();
          URL.revokeObjectURL(a.href);
        } catch (err) {
          console.error('[Floorplan] download failed', err);
          message.error('Failed to download floorplan.');
        } finally {
          setDownloadingFloorplan(false);
        }
      };

      return (
        <>
          <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
            <div className="min-w-0">
              <h1 className="text-[22px] font-bold text-[#151A30] m-0">
                Floorplan Manager
              </h1>
              <p className="text-[13px] text-black/55 m-0 mt-1">
                View 2D layouts, order CAD files, and request modifications.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-[1fr_1.55fr] gap-4 max-lg:grid-cols-1">
            <div className="rounded-3xl border border-black/[0.06] bg-white shadow-[0_4px_16px_rgba(0,0,0,0.06)] overflow-hidden flex flex-col lg:order-2">
              <div className="relative flex-1 bg-[rgb(38,38,38)] min-h-[420px] flex items-center justify-center">
                {activeLayout?.url ? (
                  <img
                    src={activeLayout.url}
                    alt={activeLayout.label}
                    className="max-w-full max-h-full object-contain p-6"
                  />
                ) : (
                  <FloorplanIcon className="w-20 h-20 text-white/30" />
                )}
              </div>
              <div className="bg-black/[0.88] text-white py-3 px-4 text-center text-[13px] font-medium">
                {activeLayout
                  ? `Viewing: ${activeLayout.label}`
                  : 'Floorplan not yet generated'}
              </div>
            </div>

            <div className="flex flex-col gap-3 lg:order-1">
              <div className="rounded-2xl bg-black/85 text-white p-4">
                <div className="flex items-center justify-between mb-2 text-white/75">
                  <span className="text-[10.5px] font-semibold uppercase tracking-[0.08em]">
                    Area Calculation
                  </span>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={editingArea ? cancelAreaEdit : startAreaEdit}
                    aria-label={editingArea ? 'Cancel edit' : 'Edit area'}
                    className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-white/10 hover:bg-white/20 transition-colors border-0 text-white/85 cursor-pointer focus:outline-none"
                  >
                    {editingArea ? (
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                      >
                        <path d="M4 4l8 8M12 4l-8 8" />
                      </svg>
                    ) : (
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M11.5 2.5l2 2-9 9-2.5.5.5-2.5 9-9z" />
                      </svg>
                    )}
                  </button>
                </div>
                {editingArea ? (
                  <input
                    autoFocus
                    type="text"
                    inputMode="decimal"
                    value={areaInput}
                    onChange={(e) => setAreaInput(e.target.value)}
                    onBlur={commitAreaEdit}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        commitAreaEdit();
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        cancelAreaEdit();
                      }
                    }}
                    placeholder="—"
                    className="w-full bg-transparent text-[24px] font-bold leading-none text-white border-0 outline-none p-0 m-0 focus:outline-none placeholder:text-white/35"
                  />
                ) : (
                  <div className="text-[24px] font-bold leading-none">
                    {areaValue != null
                      ? `${areaValue.toLocaleString()} sq ft`
                      : '— sq ft'}
                  </div>
                )}
                <div className="text-[11.5px] text-white/65 mt-1">
                  {savingArea ? 'Saving…' : 'Total area'}
                </div>
              </div>

              <div className="rounded-2xl bg-white border border-black/[0.06] p-3">
                <div className="flex items-center justify-between gap-2 px-1 pt-1 pb-2">
                  <div className="text-[13px] font-semibold text-black/85">
                    Layout Versions
                  </div>
                  {devMode && (
                    <label className="relative btn-chip text-[11px] inline-flex items-center gap-1.5 cursor-pointer">
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.75"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M8 14V6M4.5 9L8 5.5 11.5 9M3 3h10" />
                      </svg>
                      {uploadingFloorplan
                        ? floorplanUploadPct != null
                          ? `Uploading ${floorplanUploadPct}%…`
                          : 'Uploading…'
                        : 'Upload'}
                      <input
                        type="file"
                        accept="image/*"
                        className="sr-only"
                        disabled={uploadingFloorplan}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleUploadFloorplan(file);
                          e.target.value = '';
                        }}
                      />
                    </label>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  {floorplansLoading ? (
                    <div className="text-[12px] text-black/45 px-2 py-2">
                      Loading layouts…
                    </div>
                  ) : layouts.length === 0 ? (
                    <div className="text-[12px] text-black/45 px-2 py-2">
                      {devMode
                        ? 'No layouts yet — use Upload above to add one.'
                        : 'No layouts available for this tour.'}
                    </div>
                  ) : (
                    layouts.map((l, i) => {
                      const active = i === activeIdx;
                      const isVariant = usingVariants;
                      const isDefault =
                        isVariant && defaultFloorplanId === l.id;
                      const isRenaming = renamingVariant?.id === l.id;
                      return (
                        <div
                          key={l.id}
                          className={classNames(
                            'flex items-center gap-1 px-1 h-9 rounded-lg transition-colors',
                            active
                              ? 'bg-black/[0.07]'
                              : 'hover:bg-black/[0.04]',
                          )}
                        >
                          {isRenaming ? (
                            <input
                              autoFocus
                              type="text"
                              value={renamingVariant!.draft}
                              onChange={(e) =>
                                setRenamingVariant({
                                  id: l.id,
                                  draft: e.target.value,
                                })
                              }
                              onBlur={() =>
                                handleRenameFloorplan(
                                  l.id,
                                  renamingVariant!.draft,
                                )
                              }
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleRenameFloorplan(
                                    l.id,
                                    renamingVariant!.draft,
                                  );
                                } else if (e.key === 'Escape') {
                                  e.preventDefault();
                                  setRenamingVariant(null);
                                }
                              }}
                              maxLength={120}
                              className="flex-1 h-7 px-2 text-[13px] text-black/85 bg-white border border-black/15 rounded-md outline-none focus:border-black/40"
                            />
                          ) : (
                            <button
                              type="button"
                              onClick={() => setSelectedFloorplanIdx(i)}
                              className={classNames(
                                'flex-1 inline-flex items-center gap-1.5 px-2 h-9 rounded-md text-[13px] transition-colors border-0 cursor-pointer focus:outline-none text-left truncate bg-transparent',
                                active
                                  ? 'text-black/[0.9] font-semibold'
                                  : 'text-black/70 font-medium',
                              )}
                            >
                              <span className="truncate">{l.label}</span>
                              {isDefault && (
                                <span
                                  className="shrink-0 inline-flex items-center px-1.5 h-4 rounded-full text-[9.5px] font-semibold uppercase tracking-wide bg-black/[0.08] text-black/65"
                                  title="Default layout"
                                >
                                  Default
                                </span>
                              )}
                            </button>
                          )}
                          {devMode && isVariant && !isRenaming && (
                            <div className="flex items-center gap-0.5 shrink-0">
                              <button
                                type="button"
                                onClick={() => handleSetDefaultFloorplan(l.id)}
                                title={
                                  isDefault
                                    ? 'Default layout'
                                    : 'Set as default'
                                }
                                aria-label={
                                  isDefault
                                    ? 'Default layout'
                                    : 'Set as default'
                                }
                                disabled={isDefault}
                                className={classNames(
                                  'inline-flex items-center justify-center w-6 h-6 rounded-md border-0 cursor-pointer focus:outline-none bg-transparent transition-colors',
                                  isDefault
                                    ? 'text-amber-500 cursor-default'
                                    : 'text-black/40 hover:text-amber-500 hover:bg-black/[0.04]',
                                )}
                              >
                                <svg
                                  width="13"
                                  height="13"
                                  viewBox="0 0 16 16"
                                  fill={isDefault ? 'currentColor' : 'none'}
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  strokeLinejoin="round"
                                >
                                  <path d="M8 1.5l1.9 4.3 4.6.5-3.5 3.2 1 4.6L8 11.7 3.9 14.1l1-4.6L1.5 6.3l4.6-.5z" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  setRenamingVariant({
                                    id: l.id,
                                    draft: l.label,
                                  })
                                }
                                title="Rename"
                                aria-label="Rename"
                                className="inline-flex items-center justify-center w-6 h-6 rounded-md border-0 cursor-pointer focus:outline-none bg-transparent text-black/40 hover:text-black/80 hover:bg-black/[0.04]"
                              >
                                <svg
                                  width="12"
                                  height="12"
                                  viewBox="0 0 16 16"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M11.5 2.5l2 2-9 9-2.5.5.5-2.5 9-9z" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteFloorplan(l.id)}
                                title="Delete"
                                aria-label="Delete"
                                className="inline-flex items-center justify-center w-6 h-6 rounded-md border-0 cursor-pointer focus:outline-none bg-transparent text-black/40 hover:text-red-600 hover:bg-red-50"
                              >
                                <svg
                                  width="12"
                                  height="12"
                                  viewBox="0 0 16 16"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M3 4h10M6 4V2.5h4V4M5 4l.5 9h5l.5-9" />
                                </svg>
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="rounded-2xl bg-white border border-black/[0.06] p-3">
                <div className="text-[13px] font-semibold text-black/85 px-1 pt-1 pb-2">
                  Actions
                </div>
                <div className="flex flex-col gap-1.5">
                  <button
                    type="button"
                    onClick={handleDownloadFloorplan}
                    disabled={
                      downloadingFloorplan ||
                      !activeLayout?.url ||
                      activeFloorplanExists === false
                    }
                    className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-lg text-[13px] font-semibold text-white bg-black/[0.88] hover:bg-black transition-colors border-0 cursor-pointer focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M8 2v8M4.5 7L8 10.5 11.5 7M3 13h10" />
                    </svg>
                    {downloadingFloorplan
                      ? 'Downloading…'
                      : 'Download Floorplan'}
                  </button>
                  <button
                    type="button"
                    onClick={() => openRequestModal('Order CAD File')}
                    className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-lg text-[13px] font-medium text-black/80 bg-black/[0.05] hover:bg-black/[0.08] transition-colors border border-black/[0.06] cursor-pointer focus:outline-none"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M3 3h7l3 3v7H3z" />
                      <path d="M10 3v3h3" />
                    </svg>
                    Order CAD File
                  </button>
                  <button
                    type="button"
                    onClick={() => openRequestModal('Request Different Style')}
                    className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-lg text-[13px] font-medium text-black/80 bg-white hover:bg-black/[0.04] transition-colors border border-black/[0.08] cursor-pointer focus:outline-none"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M11 2.5l2.5 2.5-8 8H3v-2.5z" />
                    </svg>
                    Request Different Style
                  </button>
                  <button
                    type="button"
                    onClick={() => openRequestModal('Propose Layout Change')}
                    className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-lg text-[13px] font-medium text-black/80 bg-white hover:bg-black/[0.04] transition-colors border border-black/[0.08] cursor-pointer focus:outline-none"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M3 3h7l3 3v7H3z" />
                      <path d="M10 3v3h3M6 9h4M6 11h3" />
                    </svg>
                    Propose Layout Change
                  </button>
                  <button
                    type="button"
                    onClick={() => openRequestModal('Report an Issue')}
                    className="inline-flex items-center justify-center gap-2 h-9 mt-1 text-[12.5px] font-medium text-red-500 hover:text-red-600 bg-transparent border-0 cursor-pointer focus:outline-none"
                  >
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="8" cy="8" r="6" />
                      <path d="M8 5v3.5M8 11v.01" />
                    </svg>
                    Report an Issue
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      );
    };

    const renderVideo = () => {
      // Pure empty state — no videos, dev mode off — keeps the legacy
      // "request a video" CTA. With dev mode on (or any videos uploaded),
      // we drop into the gallery+upload UI below instead.
      if (!videosLoading && videos.length === 0 && !devMode) {
        return (
          <div className="rounded-3xl border border-dashed border-black/15 bg-black/[0.02] p-12 flex flex-col items-center justify-center text-center">
            <svg
              width="32"
              height="32"
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-black/40 mb-3"
            >
              <rect x="2.5" y="4.5" width="11" height="11" rx="1.5" />
              <path d="M13.5 8.5l4-2v7l-4-2z" />
            </svg>
            <h2 className="text-[16px] font-semibold text-black/85 m-0">
              No videos yet
            </h2>
            <p className="text-[12.5px] text-black/55 mt-1.5 m-0 max-w-sm">
              Request a custom walkthrough or highlight reel for this listing
              and our team will handle production.
            </p>
            <button
              type="button"
              onClick={() => openRequestModal('Request a Video')}
              className="mt-5 inline-flex items-center gap-1.5 h-10 px-5 rounded-full text-[13px] font-semibold text-white bg-black/85 hover:bg-black transition-colors border-0 cursor-pointer focus:outline-none"
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M10 1l1.5 5.5L17 8l-5.5 1.5L10 15l-1.5-5.5L3 8l5.5-1.5L10 1zM15.5 13l.75 2.25L18.5 16l-2.25.75L15.5 19l-.75-2.25L12.5 16l2.25-.75L15.5 13z" />
              </svg>
              Request a Video
            </button>
          </div>
        );
      }
      return (
        <section className="rounded-[20px] border border-black/10 bg-white shadow-sm overflow-hidden">
          <header className="px-6 py-4 border-b border-black/[0.08] flex items-center justify-between gap-3">
            <h2 className="text-[15px] font-semibold text-black/85 m-0">
              Videos
            </h2>
            <div className="flex items-center gap-2">
              {devMode && (
                <label className="relative btn-chip text-[11px] inline-flex items-center gap-1.5 cursor-pointer">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M8 14V6M4.5 9L8 5.5 11.5 9M3 3h10" />
                  </svg>
                  {uploadingVideo
                    ? (() => {
                        const queue = videoUploadQueue
                          ? `${videoUploadQueue.index}/${videoUploadQueue.total} `
                          : '';
                        const pct =
                          videoUploadPct != null ? `${videoUploadPct}%` : '';
                        const body = `${queue}${pct}`.trim();
                        return body ? `Uploading ${body}…` : 'Uploading…';
                      })()
                    : `Upload Videos (≤ ${Math.round(
                        MAX_VIDEO_UPLOAD_BYTES / 1024 / 1024,
                      )} MB each)`}
                  <input
                    type="file"
                    accept="video/*"
                    multiple
                    className="sr-only"
                    disabled={uploadingVideo}
                    onChange={(e) => {
                      const files = e.target.files
                        ? Array.from(e.target.files)
                        : [];
                      if (files.length) handleUploadVideos(files);
                      e.target.value = '';
                    }}
                  />
                </label>
              )}
            </div>
          </header>
          <div className="p-6">
            {videosLoading ? (
              <div className="grid grid-cols-2 gap-4 max-md:grid-cols-1">
                {[0, 1].map((i) => (
                  <div
                    key={i}
                    className="aspect-video rounded-xl bg-black/10 animate-pulse"
                  />
                ))}
              </div>
            ) : videos.length === 0 ? (
              <div className="py-10 text-center text-[12.5px] text-black/55">
                No videos yet. Use the Upload Video button to add one.
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4 max-md:grid-cols-1">
                {videos.map((v) => (
                  <div
                    key={v.id}
                    className="relative group rounded-xl overflow-hidden bg-black/95 aspect-video"
                  >
                    <video
                      controls
                      preload="metadata"
                      src={v.url}
                      className="absolute inset-0 w-full h-full object-contain bg-black"
                    />
                    <button
                      type="button"
                      onClick={() => handleDownloadVideo(v)}
                      aria-label="Download video"
                      className="absolute top-2 left-2 inline-flex items-center justify-center w-7 h-7 rounded-full bg-black/65 hover:bg-black/85 text-white border-0 cursor-pointer focus:outline-none opacity-0 group-hover:opacity-100 transition-opacity z-10"
                    >
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.9"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M8 2v8M4.5 7L8 10.5 11.5 7M3 13h10" />
                      </svg>
                    </button>
                    {devMode && (
                      <button
                        type="button"
                        onClick={() => handleDeleteVideo(v.id)}
                        aria-label="Delete video"
                        className="absolute top-2 right-2 inline-flex items-center justify-center w-7 h-7 rounded-full bg-black/65 hover:bg-black/85 text-white border-0 cursor-pointer focus:outline-none opacity-0 group-hover:opacity-100 transition-opacity z-10"
                      >
                        <svg
                          width="11"
                          height="11"
                          viewBox="0 0 16 16"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        >
                          <path d="M4 4l8 8M12 4l-8 8" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          {videos.length > 0 && (
            <footer className="px-6 py-3 border-t border-black/[0.06] flex justify-end">
              <button
                type="button"
                disabled={downloadingAllVideos}
                onClick={handleDownloadAllVideos}
                className="btn-chip text-[11px] inline-flex items-center gap-1.5"
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M8 2v8M4.5 7L8 10.5 11.5 7M3 13h10" />
                </svg>
                {downloadingAllVideos ? 'Zipping…' : 'Download All'}
              </button>
            </footer>
          )}
        </section>
      );
    };
    const renderSettings = () => {
      return (
        <>
          <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
            <h1 className="text-[22px] font-bold text-[#151A30] m-0">
              Tour Settings
            </h1>
          </div>
          <div className="rounded-[20px] border border-black/10 bg-white shadow-sm p-5 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h3 className="text-[14px] font-semibold text-black/85 m-0">
                Developer Mode
              </h3>
              <p className="text-[12.5px] text-black/55 m-0 mt-1 max-w-lg">
                Reveals upload and delete controls on this tour&rsquo;s gallery
                and video sections. Session-only — resets when you reload or
                switch tours.
              </p>
            </div>
            <Toggle checked={devMode} onChange={setDevMode} />
          </div>

          <div className="rounded-[20px] border border-black/10 bg-white shadow-sm p-5 mt-4">
            <h3 className="text-[14px] font-semibold text-black/85 m-0">
              Tour Information
            </h3>
            <dl className="m-0 mt-3 divide-y divide-black/[0.06]">
              <div className="flex items-center justify-between gap-4 py-2.5">
                <dt className="text-[12.5px] text-black/55 m-0">
                  Date when captured
                </dt>
                <dd className="text-[13px] font-medium text-black/85 m-0 text-right">
                  {formatTourDate(tour.dateCreated)}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-4 py-2.5">
                <dt className="text-[12.5px] text-black/55 m-0">
                  Date when generated
                </dt>
                <dd className="text-[13px] font-medium text-black/85 m-0 text-right">
                  {formatTourDate(tour.dateModified)}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-4 py-2.5">
                <dt className="text-[12.5px] text-black/55 m-0">
                  Tour capturer
                </dt>
                <dd className="text-[13px] font-medium text-black/85 m-0 text-right truncate max-w-[60%]">
                  {tourOwner?.displayName || tourOwner?.email || '—'}
                </dd>
              </div>
            </dl>
          </div>
        </>
      );
    };

    const SECTION_RENDER: {
      id: SectionId;
      render: () => ReactElement;
      title?: string;
    }[] = [
      { id: 'overview', render: renderOverview },
      { id: '3d-tour', render: renderTour },
      { id: 'pictures', render: renderPictures },
      { id: 'video', render: renderVideo, title: 'Video' },
      { id: 'floorplan', render: renderFloorplan },
      { id: 'settings', render: renderSettings },
    ];

    return (
      <>
        {SECTION_RENDER.map(({ id, render, title }) => (
          <section
            key={id}
            data-section={id}
            ref={(el) => {
              sectionRefs.current[id] = el;
            }}
            className="scroll-mt-20 pt-10 first:pt-2 max-md:scroll-mt-16 max-md:pt-8"
          >
            {title && (
              <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
                <h1 className="text-[22px] font-bold text-[#151A30] m-0">
                  {title}
                </h1>
              </div>
            )}
            {render()}
          </section>
        ))}
      </>
    );
  };

  return (
    <div className="flex h-full bg-[#F7F6F4] text-[#151A30]">
      <aside className="sticky top-0 h-full p-4 shrink-0 max-md:hidden">
        <div className="bg-white rounded-[2rem] shadow-[0_6px_6px_rgba(0,0,0,0.18),0_0_20px_rgba(0,0,0,0.08)] h-full w-[228px] flex flex-col p-5">
          <div className="flex items-center px-1 pb-4 border-b border-black/10 mb-4">
            <a
              href="https://www.the3dapp.com/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="The 3D App home"
              className="block w-full"
            >
              <img
                src={dashboardLogo}
                alt="The 3D App"
                className="w-full h-auto max-h-12 object-contain"
              />
            </a>
          </div>

          <button
            type="button"
            onClick={onBack}
            aria-label="Back to dashboard"
            className="flex items-center gap-2 text-[13.5px] font-medium text-black/55 hover:text-black/85 bg-transparent border-0 cursor-pointer transition-colors mb-3 w-full text-left p-0"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M10 12L6 8L10 4" />
            </svg>
            Dashboard
          </button>

          <nav className="flex-1 flex flex-col gap-0.5 overflow-y-auto -mx-1.5">
            {renderSidebarNav()}
          </nav>
        </div>
      </aside>

      <header className="md:hidden fixed top-0 left-0 right-0 z-40 h-14 bg-[#F7F6F4] flex items-center gap-2 px-3">
        <button
          type="button"
          onClick={onBack}
          aria-label="Back to dashboard"
          className="inline-flex items-center gap-1.5 text-[13px] font-medium text-black/70 bg-transparent border-0 p-1 cursor-pointer"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M10 12L6 8L10 4" />
          </svg>
          Dashboard
        </button>
        <button
          type="button"
          onClick={shareModal.toggle}
          className="ml-auto inline-flex items-center h-9 px-3.5 rounded-full text-[13px] font-semibold text-white bg-black/85 hover:bg-black transition-colors border-0 cursor-pointer focus:outline-none"
        >
          Share
        </button>
        {/* Compact section dropdown — replaces the full-screen mobile menu. */}
        <div ref={mobileMenuRef} className="relative">
          <button
            type="button"
            aria-haspopup="menu"
            aria-expanded={mobileMenuOpen}
            onClick={() => setMobileMenuOpen((o) => !o)}
            className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-[13px] font-semibold text-black/85 bg-transparent border border-black/80 hover:bg-black/[0.05] transition-colors cursor-pointer focus:outline-none"
          >
            <span>
              {SECTIONS.find((s) => s.id === activeSection)?.label ??
                'Overview'}
            </span>
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={`transition-transform ${
                mobileMenuOpen ? 'rotate-180' : ''
              }`}
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>

          {mobileMenuOpen && (
            <div
              role="menu"
              className="absolute top-full right-0 mt-2 w-56 bg-white rounded-2xl shadow-[0_8px_24px_rgba(0,0,0,0.15)] border border-black/10 py-1.5 z-50"
            >
              {SECTIONS.map((s) => {
                const active = activeSection === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => {
                      scrollToSection(s.id);
                      setMobileMenuOpen(false);
                    }}
                    className={classNames(
                      'flex items-center justify-start gap-2.5 w-full px-4 py-2.5 text-[14px] text-left transition-colors bg-transparent border-0 cursor-pointer focus:outline-none',
                      active
                        ? 'font-semibold text-black/[0.9] bg-black/[0.05]'
                        : 'font-medium text-black/75 hover:bg-black/[0.04]',
                    )}
                    aria-current={active ? 'page' : undefined}
                  >
                    <span
                      className={classNames(
                        'flex items-center justify-center w-5 h-5 shrink-0',
                        active ? 'text-black/85' : 'text-black/55',
                      )}
                    >
                      {s.icon}
                    </span>
                    <span>{s.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </header>

      <main
        ref={scrollContainerRef}
        className="flex-1 min-w-0 overflow-y-auto max-md:pt-14"
      >
        <div
          ref={sectionHeaderRef}
          className="hidden md:flex sticky top-0 z-30 items-center justify-between gap-4 px-8 py-3 bg-[#F7F6F4]/85 backdrop-blur border-b border-black/[0.06]"
        >
          <h2 className="text-[16px] font-semibold text-black/85 m-0">
            {SECTIONS.find((s) => s.id === activeSection)?.label ?? 'Overview'}
          </h2>
          <button
            type="button"
            onClick={shareModal.toggle}
            className="inline-flex items-center gap-2 h-9 px-4 rounded-full text-[13px] font-semibold text-white bg-black/85 hover:bg-black shadow-[0_4px_12px_rgba(0,0,0,0.2)] transition-colors border-0 cursor-pointer focus:outline-none"
          >
            Share
          </button>
        </div>
        <div className="px-8 pt-6 pb-10 max-md:px-4 max-md:pt-4">
          {renderSectionBody()}
        </div>
      </main>

      <ShareTourModal
        values={{ isOpened: shareModal.isOpened, close: shareModal.close }}
        tour={tour}
        layer={effectiveLayer}
        shareUrl={shareUrl}
        hideLayers={shareHideLayers}
        defaultLayerLabel={labelForLayerKey(
          defaultLayer,
          layerCustomPresets,
          slotsByName,
        )}
      />

      <RequestOrderModal
        open={requestModal.open}
        onClose={closeRequestModal}
        requestType={requestModal.type}
        tour={tour}
      />

      <ManageLayersModal
        open={manageLayersOpen}
        onClose={closeManageLayers}
        tour={tour}
        slotsByName={slotsByName}
      />

      <Modal
        visible={changeStartOpen}
        onCancel={() => setChangeStartOpen(false)}
        width={760}
        footer={null}
        closable={false}
        centered
        className="glass-modal"
        maskStyle={{
          background: 'rgba(255, 255, 255, 0.4)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
        }}
      >
        <div>
          <div className="flex items-start justify-between gap-4 mb-5">
            <div>
              <h3 className="text-[18px] font-semibold text-[#151A30] m-0">
                Choose Start Location
              </h3>
              <p className="text-[12px] text-black/55 m-0 mt-1">
                Choose the starting view visitors will see when they open this
                tour.
              </p>
            </div>
            <button
              aria-label="Close"
              onClick={() => setChangeStartOpen(false)}
              className="btn-toolbar text-black/50"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 4L12 12M12 4L4 12" />
              </svg>
            </button>
          </div>

          {startOptions.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-[13px] text-black/55 m-0">
                No start locations available for this tour.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3 max-sm:grid-cols-2 max-h-[60vh] overflow-y-auto pr-1">
              {startOptions.map((option) => {
                const isSelected =
                  option.kind === 'floorplan'
                    ? startLocation?.type === 'floorplan' &&
                      startLocation.floorName === option.floorName
                    : startLocation?.type === 'pano' &&
                      startLocation.panoId === option.id;
                const key =
                  option.kind === 'floorplan'
                    ? `fp-${option.id}`
                    : option.fullPath;
                const label =
                  option.kind === 'floorplan'
                    ? `Start at floorplan ${option.floorName}`
                    : `Start at ${option.roomName}`;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => handleSelectStart(option)}
                    aria-label={label}
                    className={classNames(
                      'relative rounded-[14px] overflow-hidden transition-all border aspect-square flex items-center justify-center',
                      option.kind === 'floorplan'
                        ? 'bg-[rgb(77,77,77)]'
                        : 'bg-black/5',
                      isSelected
                        ? 'border-black/80 ring-2 ring-black/20'
                        : 'border-black/10 hover:border-black/40 hover:-translate-y-0.5',
                    )}
                  >
                    {option.thumbnailUrl ? (
                      <img
                        src={option.thumbnailUrl}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        className={classNames(
                          'absolute inset-0 w-full h-full',
                          option.kind === 'floorplan'
                            ? 'object-contain p-4'
                            : 'object-cover',
                        )}
                      />
                    ) : (
                      <FloorplanIcon className="w-14 h-14" />
                    )}
                    {option.kind === 'floorplan' && (
                      <span className="absolute top-2 left-2 inline-flex items-center px-1.5 h-5 rounded-full bg-white/85 text-black/75 text-[10px] font-semibold uppercase tracking-wide">
                        Floorplan
                      </span>
                    )}
                    {isSelected && (
                      <span className="absolute top-2 right-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-black text-white">
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M2.5 6.5L5 9L9.5 3.5" />
                        </svg>
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}

export default TourView;
